import React, { useState, useEffect, useMemo, createContext, useContext } from 'react'
import {
  T, BottomNav, SectionLabel, Spinner, Empty, SearchBar, searchMatch, Sheet, bookName, fmtOdds,
  confColor, ScrollRow, RatingBadge, tierLabel,
} from '../nfl/ui'
import {
  getNBAStatus, getNBALeaderboard, getNBAScorecard, getNBAMeta, getNBASlate, getNBAResults,
  getNBAValidation, getNBAPlayer, askNBA, getNBASoftSpots, getNBAMoves, getNBAPushState, subscribeNBAPush, unsubscribeNBAPush,
} from './api'
import { SlipCheckButton } from './slipcheck'
import { shareBthRecord } from './share'

// NBA vertical — a full multi-tab app on the same UI pieces as NFL/CFB: Tonight (home),
// Board, Games, Injuries, Record. Isolated: it only reads /nba/* and shares nothing with
// the MLB monolith. Before any projections exist it shows the coming-soon hero with an
// EXAMPLE board (clearly labeled). No fabricated rows — honest empty states otherwise.

// Research mode (from /nba/meta): until the priced record proves an edge, pick lists are
// labeled research and VALUE tags are hidden. Lifts automatically server-side.
const ResearchCtx = createContext(null)
const useResearch = () => useContext(ResearchCtx)
const RecordCtx = createContext(null)
const useRecordStatus = () => useContext(RecordCtx)

// Collapsible section — the NFL pattern: a tappable header row (title · meta · ▾), body
// only when open. Keeps the home screen to a handful of rows instead of a long scroll.
function Section({ icon, title, meta, metaColor, defaultOpen = false, children, empty, right, onRight }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setOpen(o => !o)} aria-expanded={open} style={{ flex: 1, minWidth: 0, display: 'flex',
          alignItems: 'center', gap: 9, cursor: 'pointer', textAlign: 'left', color: T.text, background: T.card,
          border: `1px solid ${open ? T.border2 : T.border}`, borderRadius: open ? '12px 12px 0 0' : 12,
          padding: '11px 13px' }}>
          {icon && <span style={{ fontSize: 17, flexShrink: 0 }}>{icon}</span>}
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
            {meta && <span style={{ display: 'block', fontSize: 11, color: metaColor || T.muted, marginTop: 1,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta}</span>}
          </span>
          <span style={{ color: empty ? T.faint : T.green, fontWeight: 900, fontSize: 15, flexShrink: 0 }}>{open ? '▴' : '▾'}</span>
        </button>
      </div>
      {open && <div style={{ background: T.card, border: `1px solid ${T.border2}`, borderTop: 'none',
        borderRadius: '0 0 12px 12px', padding: '2px 13px 10px' }}>
        {children}
        {right && <button onClick={onRight} style={{ marginTop: 6, background: 'none', border: 'none', padding: 0,
          color: T.green, fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>{right}</button>}
      </div>}
    </div>
  )
}

function ResearchBanner() {
  const r = useResearch()
  const [open, setOpen] = useState(false)
  if (!r?.research_mode) return null
  const pct = v => v == null ? '—' : `${(v * 100).toFixed(1)}%`
  return (
    <button onClick={() => setOpen(o => !o)} style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
      marginTop: 10, padding: '8px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.08)',
      border: `1px solid rgba(245,158,11,0.30)`, fontSize: 12, color: T.muted2, lineHeight: 1.45 }}>
      <b style={{ color: T.amber }}>🧪 Model board is in research</b>
      <span style={{ color: T.muted }}> — picks below come from proven layers only {open ? '▴' : '▾'}</span>
      {open && <div style={{ marginTop: 6 }}>
        The season-average model hasn't beaten the books yet
        {r.bets ? <> (priced ROI <b style={{ color: T.text }}>{pct(r.roi)}</b>, beat the close <b style={{ color: T.text }}>{pct(r.clv_beat)}</b>, {r.clv_n} plays)</> : null}.
        Its reads are tagged WATCH. Bet tags unlock automatically once {r.unlock?.clv_n || 300}+ plays beat the close
        at {Math.round((r.unlock?.clv_beat || 0.52) * 100)}%+ with a positive ROI.</div>}
    </button>
  )
}

const G2 = '#2fe37a'
const ORANGE = '#ff8a3d'
const pctInt = p => (p == null ? '—' : `${Math.round(p * 100)}%`)
const PROPS = [
  ['', 'All'], ['points', 'Points'], ['rebounds', 'Rebounds'], ['assists', 'Assists'],
  ['threes', 'Threes'], ['pra', 'PRA'],
]
const SHORT = { points: 'PTS', rebounds: 'REB', assists: 'AST', threes: '3PM', pra: 'PRA' }
// Same six doors as NFL (This Week · Play Lab · Touchdowns · Games · Players · More).
const NBA_NAV = [['home', 'Tonight'], ['lab', 'Play Lab', 'playlab'], ['bth', 'Built to hit', 'touchdowns'],
  ['games', 'Games'], ['players', 'Players', 'players'], ['more', 'More', 'more']]
const TIER_ORDER = ['ELITE', 'BEST BET', 'STRONG', 'PLAY', 'WATCH', 'PASS']
const TIER_LABEL = { ELITE: 'Elite', 'BEST BET': 'Top Rated', STRONG: 'Strong', PLAY: 'Moderate', WATCH: 'Watch', PASS: 'Low' }

// ESPN's NBA logo codes differ from balldontlie's abbreviations for a handful of teams.
const LOGO_CODE = { GSW: 'gs', NOP: 'no', NYK: 'ny', SAS: 'sa', UTA: 'utah', WAS: 'wsh' }
const nbaLogo = abbr => `https://a.espncdn.com/i/teamlogos/nba/500/${(LOGO_CODE[abbr] || abbr || '').toLowerCase()}.png`
function Logo({ abbr, size = 24 }) {
  const [err, setErr] = useState(false)
  if (!abbr) return null
  if (err) return <span style={{ width: size, height: size, borderRadius: '50%', background: T.card2,
    border: `1px solid ${T.border2}`, display: 'inline-grid', placeItems: 'center', fontSize: size * 0.32,
    fontWeight: 800, color: T.muted, flexShrink: 0 }}>{abbr}</span>
  return <img src={nbaLogo(abbr)} alt={abbr} width={size} height={size} loading="lazy"
    onError={() => setErr(true)} style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }} />
}

// NFL-style avatar: player photo in a circle with a small team-logo badge; falls back to
// the team logo when there's no photo (or it fails to load).
function PAvatar({ url, team, size = 38 }) {
  const [bad, setBad] = useState(false)
  if (!url || bad) return (
    <span style={{ width: size, height: size, borderRadius: '50%', background: T.card2, border: `1px solid ${T.border2}`,
      display: 'inline-grid', placeItems: 'center', flexShrink: 0 }}><Logo abbr={team} size={Math.round(size * 0.66)} /></span>)
  const b = Math.max(14, Math.round(size * 0.42))
  return (
    <span style={{ position: 'relative', width: size, height: size, flexShrink: 0, display: 'inline-block' }}>
      <span style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', display: 'block', background: T.card2,
        border: `1px solid ${T.border2}` }}>
        <img src={url} alt="" loading="lazy" onError={() => setBad(true)}
          style={{ width: '135%', height: '100%', objectFit: 'cover', marginLeft: '-17.5%' }} /></span>
      <span style={{ position: 'absolute', right: -3, bottom: -3, width: b, height: b, borderRadius: '50%', background: '#0D1117',
        border: `1px solid ${T.border2}`, display: 'grid', placeItems: 'center' }}><Logo abbr={team} size={b - 4} /></span>
    </span>)
}

// balldontlie puts the tip time (ISO) in `status` until the game starts.
const tipTime = st => {
  if (!st || !/^\d{4}-\d{2}-\d{2}T/.test(st)) return null
  try {
    return new Date(st).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET'
  } catch (_) { return null }
}
const checkedAt = iso => {
  if (!iso) return null
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET' }
  catch (_) { return null }
}
const isFinal = st => /final/i.test(st || '')
const fmtDay = (d, today) => {
  if (!d) return ''
  if (d === today) return 'Tonight'
  const dt = new Date(d + 'T12:00:00')
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}
const shiftDay = (d, n) => {
  const dt = new Date((d || new Date().toISOString().slice(0, 10)) + 'T12:00:00')
  dt.setDate(dt.getDate() + n)
  return dt.toISOString().slice(0, 10)
}
const spreadTxt = g => {
  if (g.spread == null) return null
  const fav = g.spread <= 0 ? g.home : g.away
  return `${fav} ${g.spread === 0 ? 'PK' : `-${Math.abs(g.spread)}`}`
}

// Plain-English "why" for a read, built from the model's own adjustment factors.
function reasons(p) {
  const out = []
  const a = p.adj || {}
  const pctd = x => `${x > 1 ? '+' : '−'}${Math.abs(Math.round((x - 1) * 100))}%`
  const line = p.side_line ?? p.line
  if (p.proj != null && line != null) {
    const d = +(p.proj - line).toFixed(1)
    out.push(['📐', `Projects ${p.proj} ${SHORT[p.prop] || ''} vs a ${line} line (${d >= 0 ? '+' : ''}${d}).`])
  }
  if (p.proj_minutes) out.push(['⏱', `${p.proj_minutes} projected minutes.`])
  if (p.injury_adj > 1.005) out.push(['🚑', `Teammates out — minutes bumped ${pctd(p.injury_adj)}.`])
  if (p.minutes_cap) out.push(['⏳', `On a minutes restriction per the injury report — capped near ${Math.round(p.minutes_cap)} minutes; teammates absorb the rest.`])
  if (p.low_data) out.push(['🧪', `Thin sample — only ${p.sample_games} recent games (injury year or limited role). The season before is leaned on, but treat this as a rough number.`])
  if (p.injury_status === 'questionable') out.push(['⚠️', 'Listed questionable — confidence trimmed; check before tip.'])
  if (p.injury_status === 'probable') out.push(['ℹ️', 'Listed probable — expected to play.'])
  if (a.pace > 1.015) out.push(['🏃', `Fast-paced game (${pctd(a.pace)} possessions).`])
  if (a.pace && a.pace < 0.985) out.push(['🐢', `Slow-paced game (${pctd(a.pace)} possessions).`])
  if (a.defense > 1.03) out.push(['🎯', `Soft matchup — ${p.opp} allows ${pctd(a.defense)} to his position.`])
  if (a.defense && a.defense < 0.97) out.push(['🧱', `Tough matchup — ${p.opp} allows ${pctd(a.defense)} to his position.`])
  if (a.rest && a.rest < 0.99) out.push(['😮‍💨', `Short rest / back-to-back (${pctd(a.rest)}).`])
  if (a.rest > 1.01) out.push(['🛌', `Extra rest (${pctd(a.rest)}).`])
  if (a.minutes && a.minutes < 0.98) out.push(['📉', `Blowout risk trims minutes (${pctd(a.minutes)}).`])
  if (p.edge_pct != null && !p.line_suspect && p.fair_prob != null) {
    const fair = p.side === 'over' ? p.fair_prob : 1 - p.fair_prob
    out.push(['💰', `Model ${pctInt(p.side_prob)} vs the book's fair ${pctInt(fair)} — ${Math.abs(p.edge_pct)}pp edge on the ${p.side}.`])
  }
  return out
}

// ── shared bits ───────────────────────────────────────────────────────────────
function Header({ onHelp }) {
  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 100, background: T.bg, display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', padding: '14px 16px 10px', borderBottom: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 900, fontSize: 21, letterSpacing: '-0.03em', color: T.text }}>
          LINE<span style={{ color: G2 }}>BRKR</span>
        </span>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.06em', color: '#1a0d04',
          background: ORANGE, borderRadius: 6, padding: '3px 7px' }}>NBA</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {onHelp && <button onClick={onHelp} aria-label="How to read this" style={{ width: 24, height: 24, borderRadius: '50%',
          border: `1px solid ${T.border2}`, background: T.card, color: T.muted, fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>?</button>}
        <a href="?" title="Back to all sports" style={{ fontSize: 12.5, fontWeight: 700, color: T.muted, textDecoration: 'none', whiteSpace: 'nowrap' }}>‹ All sports</a>
      </div>
    </div>
  )
}

function DateBar({ date, setDate, today }) {
  const btn = { cursor: 'pointer', background: T.card, border: `1px solid ${T.border}`, color: T.muted,
    borderRadius: 8, width: 32, height: 30, fontSize: 15, fontWeight: 800 }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 2px' }}>
      <button onClick={() => setDate(shiftDay(date, -1))} style={btn} aria-label="Previous day">‹</button>
      <div style={{ flex: 1, textAlign: 'center' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{fmtDay(date, today)}</div>
        <div style={{ fontSize: 10.5, color: T.faint }}>{date}{date !== today && today ? ' · ' : ''}
          {date !== today && today && <span onClick={() => setDate(today)} style={{ color: G2, cursor: 'pointer', fontWeight: 700 }}>back to tonight</span>}
        </div>
      </div>
      <button onClick={() => setDate(shiftDay(date, 1))} style={btn} aria-label="Next day">›</button>
    </div>
  )
}

function Chip({ on, onClick, children }) {
  return (
    <button onClick={onClick} style={{ cursor: 'pointer', flexShrink: 0, fontSize: 12, fontWeight: 700,
      padding: '6px 11px', borderRadius: 8, border: `1px solid ${on ? T.green : T.border}`,
      background: on ? T.green : T.card2, color: on ? '#04140D' : T.muted }}>{children}</button>
  )
}

function Card({ children, style, onClick }) {
  return <div onClick={onClick} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
    padding: 12, cursor: onClick ? 'pointer' : 'default', ...style }}>{children}</div>
}

function InjBadge({ p }) {
  return <>
    {(p.injury_status === 'questionable' || p.injury_status === 'probable') && (
      <span title={`Injury report: ${p.injury_status}`} style={{ fontSize: 9, fontWeight: 800,
        color: '#1a0d04', background: T.amber, borderRadius: 4, padding: '1px 5px' }}>
        {p.injury_status === 'questionable' ? 'Q' : 'P'}
      </span>
    )}
    {p.minutes_cap && (
      <span title={`Injury report: minutes restriction (~${p.minutes_cap} min)`} style={{ fontSize: 9, fontWeight: 800,
        color: '#1a0d04', background: T.amber, borderRadius: 4, padding: '1px 5px' }}>MIN LIMIT {Math.round(p.minutes_cap)}</span>
    )}
    {p.low_data && (
      <span title={`Only ${p.sample_games} recent games behind this read — treat as a rough number`} style={{ fontSize: 9, fontWeight: 800,
        color: T.muted2, border: `1px dashed ${T.muted}`, borderRadius: 4, padding: '0 4px' }}>LOW DATA</span>
    )}
    {p.injury_adj > 1.005 && (
      <span title="A teammate is out — more minutes projected" style={{ fontSize: 9, fontWeight: 800,
        color: ORANGE, border: `1px solid ${ORANGE}`, borderRadius: 4, padding: '0 4px' }}>
        +{Math.round((p.injury_adj - 1) * 100)}% min
      </span>
    )}
  </>
}

function SidePill({ p }) {
  const over = p.side !== 'under'
  return (
    <span style={{ fontFamily: T.mono, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap',
      color: over ? G2 : '#7cc4ff', background: over ? 'rgba(47,227,122,0.10)' : 'rgba(124,196,255,0.10)',
      border: `1px solid ${over ? 'rgba(47,227,122,0.35)' : 'rgba(124,196,255,0.35)'}`, borderRadius: 6, padding: '2px 6px' }}>
      {over ? 'O' : 'U'} {p.side_line ?? p.line} {SHORT[p.prop] || ''}
    </span>
  )
}

// One play row — used on every list (Home, Board, Games).
function PlayRow({ p, onOpen, first }) {
  const research = useResearch()
  const hasEdge = p.edge_pct != null && !p.line_suspect
  return (
    <div onClick={() => onOpen(p)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 2px',
      borderTop: first ? 'none' : `1px solid ${T.border}`, cursor: 'pointer' }}>
      <PAvatar url={p.headshot} team={p.team} size={34} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden',
          textOverflow: 'ellipsis' }}>{p.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3, flexWrap: 'wrap' }}>
          <SidePill p={p} />
          {p.tier && <RatingBadge tier={p.tier} size={20} />}
          {p.value && !research?.research_mode && <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.04em', color: '#04140D',
            background: G2, borderRadius: 4, padding: '1px 5px' }}>VALUE</span>}
          <InjBadge p={p} />
        </div>
        <div style={{ fontSize: 10.5, color: T.muted, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden',
          textOverflow: 'ellipsis' }}>proj {p.proj} · {p.team} vs {p.opp}</div>
      </div>
      <div style={{ textAlign: 'right', minWidth: 50 }}>
        <div style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 15, color: T.green }}>{pctInt(p.side_prob)}</div>
        <div style={{ fontFamily: T.mono, fontSize: 10.5, fontWeight: 700,
          color: hasEdge ? (Math.abs(p.edge_pct) >= 2 ? G2 : T.muted) : T.faint }}>
          {hasEdge ? `${Math.abs(p.edge_pct)}pp edge` : `conf ${p.confidence || '—'}`}
        </div>
      </div>
    </div>
  )
}

const LOG_KEY = { points: 'pts', rebounds: 'reb', assists: 'ast', threes: '3pm' }
const logVal = (g, prop) => prop === 'pra' ? (g.pts || 0) + (g.reb || 0) + (g.ast || 0) : (g[LOG_KEY[prop]] || 0)

// NFL PlayerCard tabs: Intel · Trends · Form · Usage · Matchup (+ Books, NBA's line shop).
const SHEET_TABS = ['Intel', 'Trends', 'Form', 'Usage', 'Matchup', 'Books']
function SheetTabs({ tab, setTab }) {
  return (
    <div style={{ display: 'flex', gap: 2, marginTop: 14, borderBottom: `1px solid ${T.border}`, overflowX: 'auto', scrollbarWidth: 'none' }}>
      {SHEET_TABS.map(t => (
        <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: '8px 4px', border: 'none', background: 'none',
          cursor: 'pointer', fontSize: 12.5, fontWeight: tab === t ? 800 : 600, color: tab === t ? T.green : T.muted,
          borderBottom: `2px solid ${tab === t ? T.green : 'transparent'}`, marginBottom: -1 }}>{t}</button>
      ))}
    </div>
  )
}

// ── Favorites (NFL "Your players"): starred players, kept on this device ─────────
const FAV_KEY = 'nba_favs'
const readFavs = () => { try { return JSON.parse(localStorage.getItem(FAV_KEY) || '[]') } catch (_) { return [] } }
function FavStar({ id }) {
  const [on, setOn] = useState(() => readFavs().includes(id))
  const flip = e => {
    e.stopPropagation()
    const cur = readFavs(), next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]
    try { localStorage.setItem(FAV_KEY, JSON.stringify(next)) } catch (_) {}
    setOn(next.includes(id))
  }
  return <button onClick={flip} title={on ? 'Unstar' : 'Star this player'} style={{ background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 22, color: on ? '#FBBF24' : T.faint, padding: 4 }}>{on ? '★' : '☆'}</button>
}
const sharePick = txt => { try { if (navigator.share) navigator.share({ text: txt }); else navigator.clipboard.writeText(txt) } catch (_) {} }

const quant = (arr, q) => { if (!arr.length) return null; const a = [...arr].sort((x, y) => x - y); return a[Math.min(a.length - 1, Math.floor(q * a.length))] }
function TrendsPanel({ data, p, pr }) {
  const log = data?.log || []
  if (!log.length) return <Empty message="No games logged yet." />
  const vals = log.map(g => logVal(g, p.prop))
  const cur = log.filter(g => g.season === log[0].season).map(g => logVal(g, p.prop))
  const avg = a => a.length ? (a.reduce((x, y) => x + y, 0) / a.length) : null
  const l5 = avg(vals.slice(0, 5)), seas = avg(cur.length ? cur : vals)
  const up = l5 != null && seas != null ? l5 - seas : 0
  const tile = (label, v, c) => <div style={{ flex: 1, textAlign: 'center' }}>
    <div style={{ fontFamily: T.mono, fontSize: 17, fontWeight: 900, color: c || T.text }}>{v ?? '—'}</div>
    <div style={{ fontSize: 9.5, color: T.muted, letterSpacing: '.06em' }}>{label}</div></div>
  return (
    <div style={{ marginTop: 10 }}>
      <HitStrip hr={pr?.hit_rate} p={p} />
      <SectionLabel>Range · last {vals.length}</SectionLabel>
      <Card style={{ display: 'flex', padding: '10px 4px' }}>
        {tile('FLOOR', quant(vals, 0.1))}{tile('MEDIAN', quant(vals, 0.5), T.green)}{tile('CEILING', quant(vals, 0.9))}{tile('PROJ', p.proj, T.green)}
      </Card>
      <SectionLabel>Trend</SectionLabel>
      <Card style={{ fontSize: 13, color: T.muted2, lineHeight: 1.5 }}>
        Last 5: <b style={{ color: T.text }}>{l5?.toFixed(1)}</b> {SHORT[p.prop]} vs <b style={{ color: T.text }}>{seas?.toFixed(1)}</b> {cur.length ? 'this season' : 'average'}
        {' '}— <b style={{ color: up >= 0 ? T.green : T.red }}>{up >= 0 ? '▲' : '▼'} {Math.abs(up).toFixed(1)}</b>
        {Math.abs(up) >= 2 ? (up > 0 ? ' · running hot' : ' · cooling off') : ' · steady'}.
      </Card>
    </div>
  )
}
function UsagePanel({ data, p }) {
  const trend = data?.minutes_trend || []
  const mx = Math.max(40, ...trend)
  const l5 = trend.slice(-5), avg5 = l5.length ? l5.reduce((a, b) => a + b, 0) / l5.length : null
  const sp = data?.splits
  return (
    <div style={{ marginTop: 10 }}>
      <Card style={{ fontSize: 13, color: T.muted2, lineHeight: 1.55 }}>
        Projected <b style={{ color: T.text }}>{p.proj_minutes ?? '—'}</b> minutes tonight
        {avg5 != null && <> · <b style={{ color: T.text }}>{avg5.toFixed(1)}</b> over his last {l5.length}</>}.
        {(p.injury_adj || 1) > 1.03 && <> Teammates out: <b style={{ color: T.amber }}>+{Math.round((p.injury_adj - 1) * 100)}%</b> minutes on the board.</>}
        {p.minutes_cap && <> On a <b style={{ color: T.amber }}>{p.minutes_cap}-minute</b> restriction.</>}
      </Card>
      {trend.length > 1 && <>
        <SectionLabel>Minutes · last {trend.length}</SectionLabel>
        <Card style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80, padding: '8px 10px' }}>
          {trend.map((v, i) => <div key={i} title={`${v} min`} style={{ flex: 1, height: `${Math.max(4, (v / mx) * 100)}%`,
            background: i === trend.length - 1 ? T.green : T.border2, borderRadius: 3 }} />)}
        </Card>
      </>}
      {sp && <SplitsPanel data={data} />}
    </div>
  )
}
function MatchupPanel({ data, p }) {
  const a = p.adj || {}
  const [soft, setSoft] = useState(null)
  useEffect(() => { let live = true; getNBASoftSpots(2026, p.date || '').then(r => { if (live) setSoft(r) }); return () => { live = false } }, [p.date])
  const bucket = ((p.pos || '').toUpperCase().match(/[GFC]/) || ['F'])[0]
  const statKey = p.prop === 'pra' ? 'points' : p.prop
  const dv = soft?.teams?.[p.opp]
  const ordn = n => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th')}`
  const pctTxt = v => v == null ? '—' : `${v >= 1 ? '+' : ''}${Math.round((v - 1) * 100)}%`
  const vsOpp = (data?.log || []).filter(g => g.opp === p.opp)
  const row = (label, v, note) => (
    <div style={{ display: 'flex', alignItems: 'center', padding: '9px 0', borderTop: `1px solid ${T.border}` }}>
      <span style={{ flex: 1, fontSize: 13, color: T.text, fontWeight: 700 }}>{label}<span style={{ display: 'block', fontSize: 11, color: T.muted, fontWeight: 500 }}>{note}</span></span>
      <b style={{ fontFamily: T.mono, fontSize: 14, color: v == null ? T.muted : v > 1.005 ? T.green : v < 0.995 ? T.red : T.muted2 }}>{pctTxt(v)}</b>
    </div>)
  const rankRow = dv?.all?.[statKey]?.[bucket]
  return (
    <div style={{ marginTop: 10 }}>
      {rankRow && <Card style={{ marginBottom: 8, fontSize: 13, color: T.muted2, lineHeight: 1.5 }}>
        <b style={{ color: rankRow.vs_lg > 0 ? T.green : T.red }}>{p.opp} allows the {ordn(rankRow.rank)}-most {STATL[statKey] || statKey} to {POSL[bucket]}</b>
        {' '}({rankRow.allowed}/game, {rankRow.vs_lg > 0 ? '+' : ''}{rankRow.vs_lg}% vs league).</Card>}
      {dv && <Card style={{ marginBottom: 8, fontSize: 12, color: T.muted2, lineHeight: 1.6 }}>
        <span style={{ color: T.green, fontWeight: 800 }}>{p.opp} weak vs: </span>{dv.weak.map(w => `${STATL[w.stat]} to ${w.pos} (#${w.rank})`).join(' · ')}<br />
        <span style={{ color: T.red, fontWeight: 800 }}>Tough vs: </span>{dv.strong.map(w => `${STATL[w.stat]} to ${w.pos} (#${w.rank})`).join(' · ')}</Card>}
      <Card style={{ padding: '0 12px' }}>
        {row(`${p.opp} defense`, a.defense, `What ${p.opp} allows in ${SHORT[p.prop] || p.prop} to ${p.pos || 'his position'}s vs league average`)}
        {row('Pace', a.pace, "Tonight's game total vs a typical game")}
        {row('Minutes', a.minutes, 'Blowout risk from the spread')}
        {row('Rest', a.rest, 'Back-to-back trims this')}
      </Card>
      {vsOpp.length > 0 && <>
        <SectionLabel>Vs {p.opp} lately</SectionLabel>
        <Card style={{ padding: '0 12px' }}>
          {vsOpp.slice(0, 5).map((g, i) => (
            <div key={i} style={{ display: 'flex', padding: '8px 0', borderTop: i ? `1px solid ${T.border}` : 'none', fontSize: 12.5 }}>
              <span style={{ flex: 1, color: T.muted2 }}>{g.date}</span>
              <b style={{ fontFamily: T.mono, color: T.text }}>{logVal(g, p.prop)} {SHORT[p.prop]}</b>
            </div>))}
        </Card>
      </>}
    </div>
  )
}

function HitStrip({ hr, p }) {
  if (!hr) return null
  const cell = (label, h) => h ? (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div style={{ fontFamily: T.mono, fontSize: 14, fontWeight: 800,
        color: h.pct >= 0.6 ? T.green : h.pct >= 0.45 ? T.amber : T.red }}>{h.hits}/{h.n}</div>
      <div style={{ fontSize: 9.5, color: T.muted, letterSpacing: '.06em' }}>{label}</div>
    </div>
  ) : null
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11, color: T.muted, marginBottom: 6 }}>
        Cleared <b style={{ color: T.muted2 }}>{p.side === 'under' ? 'U' : 'O'}{p.side_line ?? p.line}</b> in…</div>
      <Card style={{ display: 'flex', padding: '8px 4px' }}>
        {cell('LAST 5', hr.last5)}{cell('LAST 10', hr.last10)}{cell('LAST 20', hr.last20)}{cell('ALL', hr.all)}
      </Card>
    </div>
  )
}

function BooksPanel({ pr, p }) {
  if (!pr) return <Empty message="No book prices stored for this prop yet." />
  const over = p.side !== 'under'
  const fmt = o => o == null ? '—' : fmtOdds(o)
  const row = (b, i, best) => (
    <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
      <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: T.text }}>{bookName(b.book)}
        {best && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#04140D', background: G2, borderRadius: 4, padding: '1px 5px' }}>BEST</span>}</span>
      <span style={{ fontFamily: T.mono, fontSize: 12.5, width: 46, textAlign: 'right', color: T.muted2 }}>{b.line}</span>
      <span style={{ fontFamily: T.mono, fontSize: 13, width: 56, textAlign: 'right', fontWeight: over ? 800 : 500, color: over ? T.text : T.muted }}>O {fmt(b.over)}</span>
      <span style={{ fontFamily: T.mono, fontSize: 13, width: 56, textAlign: 'right', fontWeight: over ? 500 : 800, color: over ? T.muted : T.text }}>U {fmt(b.under)}</span>
    </div>
  )
  return (
    <div>
      <div style={{ fontSize: 11.5, color: T.muted, margin: '10px 0 6px', lineHeight: 1.45 }}>
        Every book at the main line, best price for the read (<b style={{ color: T.muted2 }}>{over ? 'Over' : 'Under'}</b>) first.
        Same bet, better price = more profit when it wins.</div>
      {pr.books?.length ? <Card style={{ padding: '2px 12px' }}>{pr.books.map((b, i) => row(b, i, i === 0))}</Card>
        : <Empty message="No books at the main line." />}
      {pr.alt_lines?.length > 0 && <>
        <SectionLabel>Other lines</SectionLabel>
        <Card style={{ padding: '2px 12px' }}>{pr.alt_lines.map((b, i) => row(b, i, false))}</Card>
      </>}
    </div>
  )
}

function LogPanel({ data, p }) {
  const log = data?.log || []
  if (!log.length) return <Empty message="No games logged yet." />
  const line = p.side_line ?? p.line
  const head = { fontSize: 9.5, color: T.faint, letterSpacing: '.06em', textAlign: 'right' }
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', padding: '0 2px 6px' }}>
        <span style={{ ...head, textAlign: 'left', flex: 1 }}>DATE · OPP</span>
        {['MIN', 'PTS', 'REB', 'AST', '3PM', SHORT[p.prop] || 'PROP'].map(h => <span key={h} style={{ ...head, width: 36 }}>{h}</span>)}
      </div>
      <Card style={{ padding: '0 10px' }}>
        {log.map((g, i) => {
          const v = logVal(g, p.prop)
          const hit = line != null && (p.side === 'under' ? v < line : v > line)
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '7px 0', borderTop: i ? `1px solid ${T.border}` : 'none',
              opacity: g.season !== data.log[0].season ? 0.7 : 1 }}>
              <span style={{ flex: 1, fontSize: 11.5, color: T.muted2 }}>{g.date.slice(5)} {g.home ? 'vs' : '@'} {g.opp}
                {g.season !== log[0].season && <span style={{ color: T.faint }}> · last szn</span>}</span>
              {[Math.round(g.min || 0), g.pts, g.reb, g.ast, g['3pm']].map((x, j) => (
                <span key={j} style={{ fontFamily: T.mono, fontSize: 12, width: 36, textAlign: 'right', color: T.muted2 }}>{x ?? '—'}</span>))}
              <span style={{ fontFamily: T.mono, fontSize: 12.5, width: 36, textAlign: 'right', fontWeight: 800, color: hit ? T.green : T.red }}>{v}</span>
            </div>
          )
        })}
      </Card>
      <div style={{ fontSize: 10.5, color: T.faint, marginTop: 6 }}>Last column = this prop, green when it cleared tonight's read ({p.side === 'under' ? 'U' : 'O'}{line}).</div>
    </div>
  )
}

function SplitsPanel({ data }) {
  const sp = data?.splits
  if (!sp) return <Empty message="No splits yet." />
  const cols = [['min', 'MIN'], ['points', 'PTS'], ['rebounds', 'REB'], ['assists', 'AST'], ['threes', '3PM'], ['pra', 'PRA']]
  const trend = data.minutes_trend || []
  const mx = Math.max(40, ...trend)
  return (
    <div style={{ marginTop: 10 }}>
      <Card style={{ padding: '0 10px' }}>
        <div style={{ display: 'flex', padding: '7px 0' }}>
          <span style={{ flex: 1, fontSize: 9.5, color: T.faint }}>{(data.splits_basis || '').toUpperCase()}</span>
          {cols.map(([, h]) => <span key={h} style={{ width: 40, textAlign: 'right', fontSize: 9.5, color: T.faint }}>{h}</span>)}
        </div>
        {[['all', 'Average'], ['last10', 'Last 10'], ['home', 'Home'], ['away', 'Away']].map(([k, label]) => sp[k]?.games ? (
          <div key={k} style={{ display: 'flex', alignItems: 'center', padding: '7px 0', borderTop: `1px solid ${T.border}` }}>
            <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: T.text }}>{label} <span style={{ color: T.faint, fontWeight: 500 }}>({sp[k].games})</span></span>
            {cols.map(([c]) => <span key={c} style={{ width: 40, textAlign: 'right', fontFamily: T.mono, fontSize: 12, color: T.muted2 }}>{sp[k][c] ?? '—'}</span>)}
          </div>) : null)}
      </Card>
      {trend.length > 1 && <>
        <SectionLabel>Minutes · last {trend.length}</SectionLabel>
        <Card style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 70, padding: '8px 10px' }}>
          {trend.map((v, i) => (
            <div key={i} title={`${v} min`} style={{ flex: 1, height: `${Math.max(4, (v / mx) * 100)}%`,
              background: i === trend.length - 1 ? T.green : T.border2, borderRadius: 3 }} />))}
        </Card>
      </>}
    </div>
  )
}

function PlaySheet({ p, onClose, inSlip, onToggleSlip }) {
  const [tab, setTab] = useState('Intel')
  const [data, setData] = useState(null)
  useEffect(() => {
    if (!p?.bdl_id) return
    let live = true
    getNBAPlayer(p.bdl_id, p.date).then(d => { if (live) setData(d) })
    return () => { live = false }
  }, [p?.bdl_id, p?.date])
  if (!p) return null
  const why = reasons(p)
  const pr = (data?.props || []).find(x => x.prop === p.prop)
  const tile = (label, value, color) => (
    <div style={{ background: T.card, border: `1px solid ${T.border2}`, borderRadius: 8, padding: '8px 4px', textAlign: 'center' }}>
      <div style={{ fontFamily: T.mono, fontSize: 16, fontWeight: 800, color: color || T.text }}>{value}</div>
      <div style={{ fontSize: 9.5, color: T.muted, letterSpacing: '.08em', textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
    </div>
  )
  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: '10px 16px 26px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <PAvatar url={p.headshot} team={p.team} size={50} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: T.text, display: 'flex', gap: 6, alignItems: 'center' }}>
              {p.name} <InjBadge p={p} />
            </div>
            <div style={{ fontSize: 12, color: T.muted }}>{[p.pos, p.team, `vs ${p.opp}`].filter(Boolean).join(' · ')}</div>
          </div>
          <FavStar id={p.bdl_id} />
          <button onClick={() => sharePick(`${p.name} ${p.side === 'under' ? 'Under' : 'Over'} ${p.side_line ?? p.line} ${p.market} — ${pctInt(p.side_prob)} (LINEBRKR NBA)`)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: T.muted, padding: 4 }} title="Share">⤴</button>
        </div>
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, background: T.card3,
          border: `1px solid ${T.greenMid}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 10.5, color: T.mint, fontWeight: 800, letterSpacing: '.08em' }}>THE READ</span>
            {p.tier && <RatingBadge tier={p.tier} size={26} />}
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: T.text, marginTop: 2 }}>
            {p.side === 'under' ? 'Under' : 'Over'} {p.side_line ?? p.line} {p.market}
          </div>
          <div style={{ fontSize: 12, color: T.muted2, marginTop: 2 }}>
            {pctInt(p.side_prob)} model chance
            {p.book_line != null && p.book_used ? ` · ${bookName(p.book_used)} ${p.book_line} (${fmtOdds(p.book_over_odds)} over)` : ' · model line (no book price yet)'}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginTop: 12 }}>
          {tile('Proj', p.proj)}
          {tile('Minutes', p.proj_minutes ?? '—')}
          {tile('Edge', p.edge_pct != null && !p.line_suspect ? `${Math.abs(p.edge_pct)}pp` : '—',
            p.value ? G2 : T.text)}
          {tile('Conf', p.confidence ?? '—', confColor(p.confidence || 0))}
        </div>
        {onToggleSlip && <button onClick={() => onToggleSlip(p)} style={{ marginTop: 10, width: '100%', cursor: 'pointer', padding: '9px',
          borderRadius: 8, fontWeight: 800, fontSize: 13, border: `1px solid ${inSlip ? T.border2 : T.green}`,
          background: inSlip ? T.card2 : T.greenDim, color: inSlip ? T.muted : T.green }}>
          {inSlip ? '✓ In parlay — tap to remove' : '+ Add to parlay'}</button>}
        {pr?.best_book && pr.best_book !== p.book_used && (
          <div onClick={() => setTab('Books')} style={{ marginTop: 10, fontSize: 12, color: G2, cursor: 'pointer' }}>
            💸 Best price: {bookName(pr.best_book)} {fmtOdds(pr.best_price)} — see all books ›</div>)}
        <SheetTabs tab={tab} setTab={setTab} />
        {tab === 'Intel' && <>
          <SectionLabel>Why</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {why.map(([icon, txt], i) => (
              <div key={i} style={{ display: 'flex', gap: 10, fontSize: 13, color: T.muted2, lineHeight: 1.4 }}>
                <span style={{ width: 20, flexShrink: 0, textAlign: 'center' }}>{icon}</span><span>{txt}</span>
              </div>
            ))}
          </div>
          {p.line_suspect && <div style={{ marginTop: 12, fontSize: 11.5, color: T.amber }}>
            Book line looks off vs the model — edge hidden until it settles.</div>}
          {(data?.props || []).filter(x => x.prop !== p.prop).length > 0 && <>
            <SectionLabel>His other props tonight</SectionLabel>
            <Card style={{ padding: '2px 12px' }}>
              {data.props.filter(x => x.prop !== p.prop).map((x, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                  <span style={{ flex: 1, fontSize: 12.5, color: T.text, fontWeight: 600 }}>{x.market}</span>
                  <SidePill p={x} />
                  <span style={{ fontFamily: T.mono, fontSize: 12.5, fontWeight: 800, width: 40, textAlign: 'right', color: T.green }}>{pctInt(x.side_prob)}</span>
                  <span style={{ fontFamily: T.mono, fontSize: 11, width: 46, textAlign: 'right', color: T.muted }}>
                    {x.hit_rate?.last10 ? `${x.hit_rate.last10.hits}/${x.hit_rate.last10.n}` : ''}</span>
                </div>))}
            </Card>
          </>}
        </>}
        {tab === 'Trends' && <TrendsPanel data={data} p={p} pr={pr} />}
        {tab === 'Form' && <LogPanel data={data} p={p} />}
        {tab === 'Usage' && <UsagePanel data={data} p={p} />}
        {tab === 'Matchup' && <MatchupPanel data={data} p={p} />}
        {tab === 'Books' && <BooksPanel pr={pr} p={p} />}
        <div style={{ marginTop: 18, fontSize: 10.5, color: T.faint, lineHeight: 1.5 }}>
          Edge = model probability minus the book's no-vig price. Confidence = data quality behind the
          read, not the pick. Research, not betting advice · 21+.
        </div>
      </div>
    </Sheet>
  )
}

// Breakdown for a Built-to-hit / News-edge / Snipe pick: the reasons, in plain numbers,
// straight from his game log and tonight's board — then the full read one tap away.
const BREAK_LABEL = { dd: 'Double-double', td: 'Triple-double' }
function BreakdownSheet({ item, onClose, onOpenFull, msRecord }) {
  const [data, setData] = useState(null)
  useEffect(() => {
    let live = true
    getNBAPlayer(item.bdl_id, item.date || '').then(d => { if (live) setData(d) })
    return () => { live = false }
  }, [item.bdl_id, item.date])
  const kind = item.kind
  const prop = item.prop
  const isDD = prop === 'dd' || prop === 'td'
  const over = kind !== 'news' || item.side === 'over'
  const need = kind === 'news' ? item.line : (isDD ? null : item.threshold)
  const hits = g => {
    if (isDD) {
      const tens = [g.pts, g.reb, g.ast].filter(v => (v || 0) >= 10).length
      return tens >= (prop === 'dd' ? 2 : 3)
    }
    const v = logVal(g, prop)
    return kind === 'news' ? (over ? v > need : v < need) : v >= need
  }
  const log = data?.log || []
  const cur = log.filter(g => g.season === log[0]?.season)
  const rate = rows => rows.length ? `${rows.filter(hits).length} of ${rows.length}` : '—'
  const last5min = log.slice(0, 5).map(g => g.min || 0)
  const avgMin = last5min.length ? (last5min.reduce((a, b) => a + b, 0) / last5min.length).toFixed(1) : null
  const pr = (data?.props || []).find(x => x.prop === prop) || (data?.props || []).find(x => x.prop === 'points')
  const readTxt = kind === 'milestone' ? `${item.threshold}+ ${SHORT[prop] || prop}`
    : kind === 'news' ? `${over ? 'Over' : 'Under'} ${item.line} ${SHORT[prop] || prop}`
    : (BREAK_LABEL[prop] || `${item.threshold}+ ${SHORT[prop] || prop}`)
  const prob = item.prob
  const why = []
  if (kind === 'milestone') {
    why.push(['📈', <>Projected <b>{item.proj}</b> {SHORT[prop]} on <b>{Math.round(item.proj_minutes)}</b> minutes — he needs
      {' '}<b>{item.threshold}</b>, a cushion of <b>{(item.proj - item.threshold).toFixed(1)}</b>.</>])
  } else if (kind === 'news') {
    why.push(['📰', <>{item.news || 'Availability news'} — his projection moved <b>{item.open_mean?.toFixed(1)} → {item.mean?.toFixed(1)}</b> after
      the line posted at {item.open_line}, and the book is still at <b>{item.line}</b>.</>])
  } else {
    why.push(['🎯', <>The book prices this at <b>{fmtOdds(item.odds)}</b> ({(item.book_prob * 100).toFixed(1)}%); the model says
      {' '}<b>{(item.prob * 100).toFixed(1)}%</b>{item.why ? <> — {item.why}</> : null}.</>])
  }
  if (log.length) {
    const did = isDD ? 'Got the double' : `Cleared ${readTxt}`
    why.push(['✅', log.length <= 5
      ? <>{did} in <b>{rate(log)}</b> of his games so far.</>
      : <>{did} in <b>{rate(log.slice(0, 5))}</b> of his last 5 and <b>{rate(log.slice(0, 10))}</b> of his last {Math.min(10, log.length)}
        {cur.length > 10 ? <> · <b>{rate(cur)}</b> this season</> : null}.</>])
  }
  if (avgMin) why.push(['⏱️', <>Averaging <b>{avgMin}</b> minutes over his last {last5min.length}
    {item.proj_minutes ? <> · projected <b>{Math.round(item.proj_minutes)}</b> tonight</> : null}.</>])
  if (pr?.book_line != null) why.push(['📊', <>Book's main line: <b>{pr.book_line}</b> {SHORT[pr.prop]}
    {pr.fair_prob != null ? <> (market {Math.round(pr.fair_prob * 100)}% over)</> : null} — our projection {pr.proj}.</>])
  if ((pr?.injury_adj || 1) > 1.03) why.push(['🚑', <>Teammates out: his minutes are bumped <b>+{Math.round((pr.injury_adj - 1) * 100)}%</b> on the board.</>])
  if (kind === 'milestone' && msRecord?.graded) why.push(['🧪', <>Built-to-hit reads have hit <b>{msRecord.hits}-{msRecord.misses}</b>
    {' '}({Math.round(msRecord.hit_rate * 100)}%) — the model said {Math.round(msRecord.expected * 100)}%. Last season's replay: 87.5%.</>])
  const fake = { ...(pr || {}), prop, side: over ? 'over' : 'under', side_line: isDD ? null : (kind === 'news' ? item.line : item.threshold - 0.5) }
  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: '10px 16px 26px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <PAvatar url={item.headshot} team={item.team} size={50} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: T.text }}>{item.name}</div>
            <div style={{ fontSize: 12, color: T.muted }}>{item.team} vs {item.opp}</div>
          </div>
          <FavStar id={item.bdl_id} />
          <button onClick={() => sharePick(`${item.name} ${readTxt}${prob != null ? ` — ${Math.round(prob * 100)}%` : ''} (LINEBRKR NBA)`)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: T.muted, padding: 4 }} title="Share">⤴</button>
        </div>
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, background: T.card3, border: `1px solid ${T.greenMid}` }}>
          <div style={{ fontSize: 10.5, color: T.mint, fontWeight: 800, letterSpacing: '.08em' }}>
            {kind === 'milestone' ? '🎯 BUILT TO HIT' : kind === 'news' ? '⚡ NEWS EDGE' : '🔫 SNIPE · EXPERIMENTAL'}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 2 }}>
            <span style={{ fontSize: 22, fontWeight: 900, color: T.text }}>{readTxt}</span>
            {prob != null && <b style={{ fontFamily: T.mono, fontSize: 20, color: T.green }}>{Math.round(prob * 100)}%</b>}
          </div>
          {item.odds != null && <div style={{ fontSize: 12, color: T.muted2, marginTop: 2 }}>
            {fmtOdds(item.odds)}{item.book ? ` · ${bookName(item.book)}` : ''}{item.ev != null ? ` · EV +${Math.round(item.ev * 100)}%` : ''}</div>}
        </div>
        <SectionLabel>Why</SectionLabel>
        {!data ? <Spinner /> : <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {why.map(([icon, txt], i) => (
            <div key={i} style={{ display: 'flex', gap: 10, fontSize: 13, color: T.muted2, lineHeight: 1.45 }}>
              <span style={{ width: 20, flexShrink: 0, textAlign: 'center' }}>{icon}</span><span>{txt}</span>
            </div>))}
        </div>}
        {pr && <button onClick={() => onOpenFull(pr)} style={{ marginTop: 14, width: '100%', cursor: 'pointer', padding: '10px',
          borderRadius: 8, fontWeight: 800, fontSize: 13, border: `1px solid ${T.border2}`, background: T.card2, color: T.green }}>
          Full player page — books, splits, all props ›</button>}
        {data && !isDD && <LogPanel data={data} p={fake} />}
        <div style={{ marginTop: 14, fontSize: 10.5, color: T.faint, lineHeight: 1.5 }}>Research, not betting advice · 21+.</div>
      </div>
    </Sheet>
  )
}

function Glossary({ onClose }) {
  const rows = [
    ['Rating', 'Elite / Top Rated / Strong = recommended (needs confidence, probability, and a real edge vs the book when priced). Moderate = a lean. Same rules as NFL.'],
    ['The read', 'Over or Under at the line — the side the model favors (vs the book line when one is posted).'],
    ['Model %', "The model's chance that side cashes."],
    ['Edge (pp)', "How many percentage points the model beats the book's fair (no-vig) price. VALUE = 2pp or more."],
    ['Proj', 'Projected stat — minutes × per-minute rate × pace, rest and matchup.'],
    ['+X% min', "A teammate is out; his minutes are redistributed and this player's projection rises."],
    ['LOW DATA', 'Fewer than 20 recent games behind the read (e.g. coming back from injury). The number is rougher than usual.'],
    ['Q / P', 'On the injury report as Questionable / Probable. OUT and Doubtful players never board.'],
    ['Conf', 'Data quality behind the read (games played, minutes stability) — not how good the pick is.'],
  ]
  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: '10px 16px 26px' }}>
        <div style={{ fontSize: 17, fontWeight: 900, color: T.text, marginBottom: 10 }}>How to read the NBA board</div>
        {rows.map(([k, v]) => (
          <div key={k} style={{ padding: '9px 0', borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{k}</div>
            <div style={{ fontSize: 12.5, color: T.muted, marginTop: 2, lineHeight: 1.45 }}>{v}</div>
          </div>
        ))}
      </div>
    </Sheet>
  )
}

// ── Tonight (home) ────────────────────────────────────────────────────────────
function Hero({ data, date, today }) {
  const research = useResearch()
  const c = data?.counts || {}
  const stat = (v, l, color) => (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div style={{ fontFamily: T.mono, fontSize: 20, fontWeight: 900, color: color || T.text }}>{v ?? 0}</div>
      <div style={{ fontSize: 9.5, color: T.muted, letterSpacing: '.08em', textTransform: 'uppercase' }}>{l}</div>
    </div>
  )
  return (
    <div style={{ marginTop: 10, borderRadius: 14, padding: '16px 14px 14px',
      background: 'linear-gradient(135deg, rgba(255,138,61,0.16), rgba(0,217,126,0.08))', border: `1px solid ${T.border2}` }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: ORANGE, letterSpacing: '.1em' }}>🏀 {date === today ? "TONIGHT'S SLATE" : 'SLATE'}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: T.text, marginTop: 4, lineHeight: 1.15 }}>
        {c.games ? `${c.games} game${c.games === 1 ? '' : 's'}, ${c.props} props modeled` : 'No games on this date'}
      </div>
      {c.games > 0 && <div style={{ display: 'flex', marginTop: 14 }}>
        {research?.research_mode ? stat((data?.milestones?.plays || []).length, 'Built to hit', G2)
          : stat(c.value, 'Value edges', G2)}
        {stat(c.priced, 'Priced')}
        {stat(c.out, 'Out', T.red)}
        {stat(c.questionable, 'Questionable', T.amber)}
      </div>}
    </div>
  )
}

// Record visibility: hidden ("testing") until a week of nights is graded. Owner can
// still view it with ?showrecord=1.
const showRecordOverride = () => { try { return new URLSearchParams(window.location.search).get('showrecord') === '1' } catch (_) { return false } }

function RecordTesting({ rec }) {
  const n = rec?.nights_graded ?? 0, need = rec?.nights_needed ?? 7
  return (
    <Card style={{ marginTop: 10, textAlign: 'center', padding: '22px 16px' }}>
      <div style={{ fontSize: 26 }}>🧪</div>
      <div style={{ fontSize: 17, fontWeight: 900, color: T.text, marginTop: 4 }}>Record — testing</div>
      <div style={{ fontSize: 12.5, color: T.muted, marginTop: 6, lineHeight: 1.5 }}>
        Every Built-to-hit read is graded on the box score after the final buzzer. The public record opens after
        {' '}{need} nights and a real sample — <b style={{ color: T.muted2 }}>{Math.min(n, need)} of {need}</b> nights graded so far.</div>
    </Card>
  )
}

function TrustBar({ rec, goRecord }) {
  const b = rec?.built_to_hit || {}
  return (
    <Card onClick={goRecord} style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px' }}>
      <span style={{ fontSize: 18 }}>🎯</span>
      <div style={{ flex: 1, fontSize: 12.5, color: T.muted2, lineHeight: 1.35 }}>
        {b.graded
          ? <>Built to hit <b style={{ fontFamily: T.mono, color: T.text }}>{b.hits}-{b.misses}</b>
              {' '}· <b style={{ color: T.green }}>{pctInt(b.hit_rate)}</b> hit rate</>
          : <>Every read is graded after the final buzzer.</>}
      </div>
      <span style={{ color: T.green, fontSize: 12, fontWeight: 700 }}>Record ›</span>
    </Card>
  )
}

function MarketCard({ p, onOpen }) {
  return (
    <div onClick={() => onOpen(p)} style={{ width: 150, flexShrink: 0, background: T.card, border: `1px solid ${T.border}`,
      borderRadius: 12, padding: 10, cursor: 'pointer' }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: T.muted, letterSpacing: '.08em' }}>{(p.market || '').toUpperCase()}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
        <Logo abbr={p.team} size={20} />
        <div style={{ fontSize: 13, fontWeight: 800, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
      </div>
      <div style={{ marginTop: 6 }}><SidePill p={p} /></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontFamily: T.mono, fontSize: 12 }}>
        <span style={{ color: T.green, fontWeight: 800 }}>{pctInt(p.side_prob)}</span>
        <span style={{ color: p.value ? G2 : T.faint, fontWeight: 700 }}>
          {p.edge_pct != null && !p.line_suspect ? `${Math.abs(p.edge_pct)}pp` : `proj ${p.proj}`}</span>
      </div>
    </div>
  )
}

function GameMini({ g, onClick }) {
  const tip = tipTime(g.status)
  return (
    <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 2px',
      borderTop: `1px solid ${T.border}`, cursor: 'pointer' }}>
      <Logo abbr={g.away} size={22} /><span style={{ fontSize: 13, fontWeight: 800, color: T.text, width: 36 }}>{g.away}</span>
      <span style={{ fontSize: 11, color: T.faint }}>@</span>
      <Logo abbr={g.home} size={22} /><span style={{ fontSize: 13, fontWeight: 800, color: T.text, width: 36 }}>{g.home}</span>
      <span style={{ flex: 1, fontSize: 11, color: T.muted, textAlign: 'right' }}>
        {isFinal(g.status) && g.home_score != null ? `Final ${g.away_score}-${g.home_score}`
          : [tip, spreadTxt(g), g.total != null ? `O/U ${g.total}` : null].filter(Boolean).join(' · ')}
      </span>
    </div>
  )
}

// Built to hit: the highest round number each player clears with ~80%+ probability. The
// model is well calibrated at these thresholds (said 84% → hit 82% over 2,665 player-games),
// so this is the "be right" list — graded on its own hit rate.
function Milestones({ ms, limit = 6, onBreak, date }) {
  const [all, setAll] = useState(false)
  const plays = ms?.plays || []
  const shown = all ? plays : plays.slice(0, limit)
  if (!plays.length) return <div style={{ fontSize: 12, color: T.muted, padding: '10px 0' }}>No milestone reads for this date yet.</div>
  return (
    <>
      {shown.map((x, i) => (
        <div key={i} onClick={() => onBreak && onBreak({ ...x, kind: 'milestone', date })} style={{ display: 'flex',
          alignItems: 'center', gap: 10, padding: '9px 0', cursor: 'pointer', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
          <PAvatar url={x.headshot} team={x.team} size={34} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {x.name} <span style={{ color: T.green }}>{x.threshold}+ {SHORT[x.prop] || x.prop}</span></div>
            <div style={{ fontSize: 11, color: T.muted }}>v {x.opp} · proj {x.proj} · {Math.round(x.proj_minutes)} min
              {x.book_line != null ? ` · line ${x.book_line}` : ''}</div>
          </div>
          <b style={{ fontFamily: T.mono, fontSize: 15, color: T.green }}>{Math.round(x.prob * 100)}%</b>
          <span style={{ color: T.faint, fontSize: 14 }}>›</span>
        </div>))}
      {plays.length > limit && <button onClick={() => setAll(v => !v)} style={{ width: '100%', marginTop: 4, padding: '7px 0',
        background: T.card2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.green, fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
        {all ? 'Show fewer ▴' : `Show all ${plays.length} ▾`}</button>}
      <div style={{ fontSize: 10.5, color: T.faint, paddingTop: 6 }}>
        Alt-line style: shorter odds than the main line, built to be right. Graded nightly.</div>
    </>
  )
}
const msMeta = ms => {
  const r = ms?.record
  return r?.graded ? `${r.hits}-${r.misses} · ${Math.round(r.hit_rate * 100)}% hit · ${(ms?.plays || []).length} tonight`
    : `${(ms?.plays || []).length} tonight`
}

// ── Built to hit tab — the NFL Touchdowns layout for NBA: ranked cards, signal chips,
// position + signal filters, search, sort, "+" to build a parlay, tap for the breakdown.
const BTH_SORTS = [['prob', 'To hit'], ['cushion', 'Cushion'], ['l10', 'Last 10'], ['min', 'Minutes'], ['tt', 'Team total']]
const BTH_FILTERS = [['out', '🚑 Teammates out', x => (x.bump || 0) >= 3], ['soft', '🟢 Soft matchup', x => (x.matchup || 0) >= 3],
  ['tt', '📈 High team total', x => (x.team_total || 0) >= 117], ['l10', '🔥 10/10 lately', x => x.l10_n >= 5 && x.l10_hits === x.l10_n]]
const msLeg = x => ({ ...x, side: 'over', side_line: x.threshold - 0.5, line: x.threshold - 0.5, side_prob: x.prob,
  market: `${x.threshold}+ ${SHORT[x.prop] || x.prop}`, prop: x.prop })
function Chip2({ label, value, color }) {
  return <span style={{ display: 'inline-flex', gap: 5, alignItems: 'baseline', fontSize: 12, color: T.muted2, background: T.card2,
    border: `1px solid ${T.border}`, borderRadius: 8, padding: '5px 9px' }}>{label} <b style={{ color: color || T.green, fontFamily: T.mono }}>{value}</b></span>
}
function BthCard({ x, rank, onBreak, inSlip, onToggle }) {
  const cush = (x.proj - x.threshold)
  return (
    <div onClick={() => onBreak({ ...x, kind: 'milestone' })} style={{ cursor: 'pointer', background: T.card, borderRadius: 14,
      border: `1px solid ${rank <= 3 ? T.greenMid : T.border2}`, padding: '12px 12px 10px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <b style={{ fontSize: 17, color: T.green, width: 30 }}>#{rank}</b>
        <PAvatar url={x.headshot} team={x.team} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.name}</div>
          <div style={{ fontSize: 12, color: T.muted }}>{[x.pos, `${x.team} vs ${x.opp}`].filter(Boolean).join(' · ')}</div>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.green, marginTop: 1 }}>{x.threshold}+ {SHORT[x.prop] || x.prop}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: T.mono, fontSize: 21, fontWeight: 900, color: T.green }}>{(x.prob * 100).toFixed(1)}%</div>
          <div style={{ fontSize: 9.5, color: T.muted, letterSpacing: '.1em' }}>TO HIT</div>
        </div>
        <button onClick={e => { e.stopPropagation(); onToggle(msLeg(x)) }} style={{ width: 36, height: 36, borderRadius: 9, cursor: 'pointer',
          border: `1.5px solid ${T.green}`, background: inSlip ? T.green : 'transparent', color: inSlip ? '#04140D' : T.green,
          fontSize: 18, fontWeight: 900, flexShrink: 0 }}>{inSlip ? '✓' : '+'}</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
        <Chip2 label="Proj" value={x.proj} />
        <Chip2 label="Cushion" value={`+${cush.toFixed(1)}`} />
        {x.l10_n > 0 && <Chip2 label={`Last ${x.l10_n}`} value={`${x.l10_hits}/${x.l10_n}`}
          color={x.l10_hits / x.l10_n >= 0.8 ? T.green : x.l10_hits / x.l10_n >= 0.6 ? T.amber : T.red} />}
        <Chip2 label="Min" value={Math.round(x.proj_minutes)} color={T.text} />
        {x.book_line != null && <Chip2 label="Line" value={x.book_line} color={T.text} />}
        {x.matchup ? <Chip2 label="Matchup" value={`${x.matchup > 0 ? '+' : ''}${x.matchup}%`} color={x.matchup > 0 ? T.green : T.amber} /> : null}
        {x.team_total != null && <Chip2 label="Team total" value={x.team_total} color={x.team_total >= 117 ? T.green : T.text} />}
        {x.bump >= 3 && <Chip2 label="Teammates out" value={`+${x.bump}% min`} color={T.amber} />}
      </div>
    </div>
  )
}
function BuiltToHitTab({ season, date, onBreak, inSlip, onToggle, initProp = '', initFlt = '' }) {
  const [d, setD] = useState(null)
  const [pos, setPos] = useState('ALL')
  const [prop, setProp] = useState(initProp)
  const [flt, setFlt] = useState(initFlt ? { [initFlt]: true } : {})
  const [q, setQ] = useState('')
  const [sort, setSort] = useState('prob')
  useEffect(() => { let live = true; setD(null); getNBASlate(season, date, 2).then(r => { if (live) setD(r) }); return () => { live = false } }, [season, date])
  if (!d) return <Spinner />
  const ms = d.milestones || {}
  let rows = ((prop && ms.by_prop?.[prop]) || ms.plays || []).map(x => ({ ...x, date }))
  if (pos !== 'ALL') rows = rows.filter(x => (x.pos || '').toUpperCase().includes(pos))
  if (prop) rows = rows.filter(x => x.prop === prop)
  for (const [k, , fn] of BTH_FILTERS) if (flt[k]) rows = rows.filter(fn)
  if (q) rows = rows.filter(x => searchMatch(q, [x.name, x.team, x.opp]))
  const key = { prob: x => x.prob, cushion: x => x.proj - x.threshold, l10: x => x.l10_n ? x.l10_hits / x.l10_n : 0,
    min: x => x.proj_minutes || 0, tt: x => x.team_total || 0 }[sort]
  rows = [...rows].sort((a, b) => key(b) - key(a))
  const r = ms.record
  const pill = (on, label, onClick) => <button onClick={onClick} style={{ cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 800,
    padding: '8px 14px', borderRadius: 18, border: `1px solid ${on ? T.green : T.border2}`, background: on ? T.green : T.card,
    color: on ? '#04140D' : T.text }}>{label}</button>
  return (
    <div>
      <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 14, background: 'linear-gradient(120deg,#0c2018,#0a1119)',
        border: `1px solid ${T.green}55` }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: T.text }}>🎯 Built to hit</div>
        <div style={{ fontSize: 12, color: T.muted2, marginTop: 3, lineHeight: 1.45 }}>The highest number each player clears ~80%+ of the time
          {r?.graded ? <> · <b style={{ color: T.green }}>{r.hits}-{r.misses} ({Math.round(r.hit_rate * 100)}%)</b> this season · 87.5% on last season's replay</> : null}</div>
      </div>
      <div style={{ fontSize: 12, color: T.muted, margin: '10px 2px 8px', lineHeight: 1.45 }}>
        Every read with the signals behind it — recent hit rate at that exact number · minutes · matchup · team total. Filter, then tap for the full breakdown.</div>
      <ScrollRow style={{ gap: 8 }}>{['ALL', 'G', 'F', 'C'].map(p => <span key={p}>{pill(pos === p, p === 'ALL' ? 'All' : p, () => setPos(p))}</span>)}
        {PROPS.slice(1).map(([k, l]) => <span key={k}>{pill(prop === k, l, () => setProp(prop === k ? '' : k))}</span>)}</ScrollRow>
      <ScrollRow style={{ gap: 8, marginTop: 8 }}>{BTH_FILTERS.map(([k, l]) => <span key={k}>{pill(!!flt[k], l, () => setFlt({ ...flt, [k]: !flt[k] }))}</span>)}</ScrollRow>
      <SearchBar value={q} onChange={setQ} placeholder="Search player, team, or opponent…" style={{ marginTop: 10 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto', margin: '10px 0 6px', scrollbarWidth: 'none' }}>
        <span style={{ fontSize: 12, color: T.muted, marginRight: 2 }}>Sort</span>
        {BTH_SORTS.map(([k, l]) => <button key={k} onClick={() => setSort(k)} style={{ cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 12.5,
          fontWeight: 800, padding: '6px 12px', borderRadius: 8, border: `1.5px solid ${sort === k ? T.green : T.border2}`,
          background: sort === k ? T.greenDim : T.card2, color: sort === k ? T.green : T.muted2 }}>{l}</button>)}
      </div>
      <div style={{ fontSize: 12, color: T.muted, margin: '4px 2px 10px' }}>{rows.length} reads · <span style={{ color: T.green }}>tap + to build a parlay</span></div>
      {!rows.length ? <Empty message="No reads match these filters." />
        : rows.map((x, i) => <BthCard key={`${x.bdl_id}${x.prop}`} x={x} rank={i + 1} onBreak={onBreak}
            inSlip={inSlip(msLeg(x))} onToggle={onToggle} />)}
    </div>
  )
}

// ── Play Lab — NFL's "Generate a play": pick scope + stat + how many → ranked plays ──
function PlayLabTab({ season, date, onBreak, inSlip, onToggle }) {
  const [d, setD] = useState(null)
  const [scope, setScope] = useState([])
  const [prop, setProp] = useState('')
  const [n, setN] = useState(5)
  const [run, setRun] = useState(false)
  useEffect(() => { let live = true; setD(null); getNBASlate(season, date, 2).then(r => { if (live) setD(r) }); return () => { live = false } }, [season, date])
  if (!d) return <Spinner />
  const games = d.games || []
  const pool = ((prop && d.milestones?.by_prop?.[prop]) || d.milestones?.plays || []).map(x => ({ ...x, date }))
    .filter(x => (!scope.length || scope.includes(x.game_id)) && (!prop || x.prop === prop))
    .sort((a, b) => b.prob - a.prob || (b.proj - b.threshold) - (a.proj - a.threshold)).slice(0, n)
  const chip = (on, label, onClick) => <button key={label} onClick={onClick} style={{ cursor: 'pointer', fontSize: 13, fontWeight: 800,
    padding: '8px 13px', borderRadius: 10, border: `1.5px solid ${on ? T.green : T.border2}`, background: on ? T.greenDim : T.card2,
    color: on ? T.green : T.text }}>{label}</button>
  const step = (k, t) => <div style={{ fontSize: 12, fontWeight: 900, color: T.muted, letterSpacing: '.08em', margin: '16px 2px 8px' }}>{k} · {t}</div>
  return (
    <div>
      <CtaCard primary icon="🧪" title="Play Lab" sub="Pick the games + a stat — get the highest-probability plays, ranked by the Built-to-hit model." onClick={() => setRun(true)} />
      {step('1', 'GAMES')}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {chip(!scope.length, 'All games', () => setScope([]))}
        {games.map(g => chip(scope.includes(g.game_id), `${g.away} @ ${g.home}`,
          () => setScope(scope.includes(g.game_id) ? scope.filter(x => x !== g.game_id) : [...scope, g.game_id])))}
      </div>
      {step('2', 'STAT')}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {[['', 'Any'], ['points', 'Points'], ['rebounds', 'Rebounds'], ['assists', 'Assists'], ['threes', 'Threes'], ['pra', 'PRA']].map(([k, l]) => chip(prop === k, l, () => setProp(k)))}
      </div>
      {step('3', 'HOW MANY')}
      <div style={{ display: 'flex', gap: 8 }}>{[5, 10, 15].map(k => chip(n === k, `Top ${k}`, () => setN(k)))}</div>
      <button onClick={() => setRun(true)} style={{ width: '100%', marginTop: 16, padding: '13px', borderRadius: 12, border: 'none', cursor: 'pointer',
        background: T.green, color: '#04140D', fontSize: 15, fontWeight: 900 }}>Generate</button>
      {run && <>
        <div style={{ fontSize: 12, color: T.muted, margin: '14px 2px 10px' }}>{pool.length} plays · highest probability first ·
          <span style={{ color: T.green }}> tap + to build a parlay</span></div>
        {!pool.length ? <Empty message="Nothing matches — widen the games or the stat." />
          : pool.map((x, i) => <BthCard key={`${x.bdl_id}${x.prop}`} x={x} rank={i + 1} onBreak={onBreak}
              inSlip={inSlip(msLeg(x))} onToggle={onToggle} />)}
      </>}
    </div>
  )
}

// ── Ask BRKR — NFL's plain-question box, on NBA's board ─────────────────────
function AskBrkr({ season, date, onBreak }) {
  const [q, setQ] = useState('')
  const [res, setRes] = useState(null)
  const [busy, setBusy] = useState(false)
  const go = async (text) => {
    const t = (text ?? q).trim(); if (!t) return
    setQ(t); setBusy(true); setRes(await askNBA(t, season, date)); setBusy(false)
  }
  const ex = ['Safest threes tonight', 'Safest rebounds', 'Jokic', "Who's out for BOS"]
  return (
    <div style={{ marginTop: 12, padding: '12px 13px', borderRadius: 14, background: T.card, border: `1px solid ${T.border2}` }}>
      <div style={{ fontSize: 15, fontWeight: 900, color: T.text }}>💬 Ask BRKR</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && go()} placeholder="Ask about a player, team or stat…"
          style={{ flex: 1, minWidth: 0, background: T.card2, color: T.text, border: `1px solid ${T.border2}`, borderRadius: 10, padding: '10px 12px', fontSize: 13.5 }} />
        <button onClick={() => go()} style={{ cursor: 'pointer', border: 'none', borderRadius: 10, background: T.green, color: '#04140D', fontWeight: 900, padding: '0 14px' }}>{busy ? '…' : 'Ask'}</button>
      </div>
      {!res && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        {ex.map(e => <button key={e} onClick={() => go(e)} style={{ cursor: 'pointer', fontSize: 11.5, color: T.muted2, background: T.card2,
          border: `1px solid ${T.border}`, borderRadius: 14, padding: '4px 10px' }}>{e}</button>)}</div>}
      {res && <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 13, color: T.text, lineHeight: 1.45 }}>{res.answer}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
          {(res.plays || []).slice(0, 8).map((x, i) => <MiniRow key={i} team={x.team} shot={x.headshot || null} top={x.name}
            sub={`${x.threshold}+ ${SHORT[x.prop]} · vs ${x.opp}`} right={`${Math.round(x.prob * 100)}%`} onClick={() => onBreak({ ...x, kind: 'milestone', date })} />)}
          {(res.snipes || []).map((x, i) => <MiniRow key={`s${i}`} team={x.team} shot={x.headshot || null} top={x.name} sub={x.label}
            right={fmtOdds(x.odds)} onClick={() => onBreak({ ...x, kind: 'snipe' })} />)}
          {(res.injuries || []).length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {res.injuries.map((i, k) => <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: T.text,
              background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: '3px 8px 3px 4px' }}>
              <Logo abbr={i.team} size={16} />{i.name}<span style={{ color: T.red }}> {i.status}</span></span>)}</div>}
        </div>
      </div>}
    </div>
  )
}

// ── Soft spots / Matchups — NBA's "weak against the deep ball" ────────────────
const POSL = { G: 'guards', F: 'forwards', C: 'centers' }
const STATL = { points: 'PTS', rebounds: 'REB', assists: 'AST', threes: '3PM' }
function SpotRow({ sp, onBreak, date, compact }) {
  return (
    <div style={{ padding: '10px 0', borderTop: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Logo abbr={sp.defense} size={22} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 900, color: T.text }}>{sp.headline}</div>
          <div style={{ fontSize: 11, color: T.muted }}>{sp.allowed} {STATL[sp.stat]}/game to {POSL[sp.pos]} ·
            <b style={{ color: T.green }}> +{sp.vs_lg}%</b> vs league · {sp.offense} @ {sp.defense === sp.offense ? '' : sp.defense}</div>
        </div>
        <span style={{ fontFamily: T.mono, fontSize: 12, fontWeight: 900, color: T.green, background: T.greenDim,
          borderRadius: 6, padding: '3px 6px' }}>#{sp.rank}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 7 }}>
        {sp.players.slice(0, compact ? 1 : 4).map((a, i) => (
          <MiniRow key={i} team={a.team} shot={a.headshot || null} top={a.name}
            sub={a.threshold ? `${a.threshold}+ ${STATL[a.prop]} · proj ${a.proj}` : `proj ${a.proj} ${STATL[a.prop]}${a.book_line != null ? ` · line ${a.book_line}` : ''}`}
            right={a.prob ? `${Math.round(a.prob * 100)}%` : `+${a.matchup}%`} rightColor={a.prob ? T.green : T.amber}
            onClick={a.threshold ? () => onBreak({ ...a, kind: 'milestone', date }) : undefined} />))}
      </div>
    </div>
  )
}
function MatchupsTab({ season, date, onBreak }) {
  const [d, setD] = useState(null)
  const [game, setGame] = useState(null)
  useEffect(() => { let live = true; setD(null); getNBASoftSpots(season, date).then(r => { if (live) setD(r) }); return () => { live = false } }, [season, date])
  if (!d) return <Spinner />
  const spots = (d.spots || []).filter(x => !game || x.game_id === game)
  const games = [...new Map((d.spots || []).map(x => [x.game_id, x])).values()]
  const teams = Object.entries(d.teams || {})
  return (
    <div>
      <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 14, background: 'linear-gradient(120deg,#0c2018,#0a1119)', border: `1px solid ${T.green}55` }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: T.text }}>🎯 Soft spots</div>
        <div style={{ fontSize: 12, color: T.muted2, marginTop: 3, lineHeight: 1.45 }}>Where each defense gives up the most — a stat to a position, ranked 1-30
          (#1 = most allowed) — and the players attacking it tonight. Early season blends in last year's defense.</div>
      </div>
      <ScrollRow style={{ gap: 8, marginTop: 10 }}>
        <button onClick={() => setGame(null)} style={{ cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 800, padding: '7px 13px', borderRadius: 16,
          border: `1px solid ${!game ? T.green : T.border2}`, background: !game ? T.green : T.card, color: !game ? '#04140D' : T.text }}>All games</button>
        {games.map(g => <button key={g.game_id} onClick={() => setGame(g.game_id)} style={{ cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 800,
          padding: '7px 13px', borderRadius: 16, border: `1px solid ${game === g.game_id ? T.green : T.border2}`,
          background: game === g.game_id ? T.green : T.card, color: game === g.game_id ? '#04140D' : T.text }}>{[g.offense, g.defense].sort().join(' · ')}</button>)}
      </ScrollRow>
      <Card style={{ padding: '0 12px', marginTop: 10 }}>
        {!spots.length ? <div style={{ fontSize: 12, color: T.muted, padding: '12px 0' }}>No standout soft spots for this game.</div>
          : spots.map((sp, i) => <SpotRow key={i} sp={sp} onBreak={onBreak} date={date} />)}
      </Card>
      <SectionLabel>Defense report · every team tonight</SectionLabel>
      {teams.map(([t, v]) => (
        <Card key={t} style={{ padding: '10px 12px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Logo abbr={t} size={22} /><b style={{ fontSize: 14 }}>{t} defense</b></div>
          <div style={{ fontSize: 12, color: T.muted2, marginTop: 6, lineHeight: 1.6 }}>
            <span style={{ color: T.green, fontWeight: 800 }}>Weak vs: </span>
            {v.weak.map(w => `${STATL[w.stat]} to ${w.pos} (#${w.rank}, +${w.vs_lg}%)`).join(' · ')}<br />
            <span style={{ color: T.red, fontWeight: 800 }}>Tough vs: </span>
            {v.strong.map(w => `${STATL[w.stat]} to ${w.pos} (#${w.rank}, ${w.vs_lg}%)`).join(' · ')}
          </div>
        </Card>))}
    </div>
  )
}

// ── More — NFL's "Deeper tools" list ─────────────────────────────────────────
function MoreTab({ go, onHelp, season, date, onOpen }) {
  const tools = [
    ['📊', 'Record', 'Every graded read — honest, at the posted price', () => go('record')],
    ['🎯', 'Soft spots · matchups', 'Where each defense breaks down and who attacks it', () => go('matchups')],
    ['🚑', 'Injury report', "Who's out tonight and whose minutes go up", () => go('injuries')],
    ['🧪', 'Model research board', "The season-average model's reads by game (in research)", () => go('top')],
    ['📖', 'How to read LINEBRKR NBA', 'What every number and badge means', onHelp],
    ['🔗', 'Public track record', 'The shareable proof page', () => { try { window.location.href = '/nba/proof' } catch (_) {} }],
  ]
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 900, color: T.muted, letterSpacing: '.08em', margin: '14px 2px 8px' }}>DEEPER TOOLS</div>
      {tools.map(([icon, t, s, fn]) => (
        <button key={t} onClick={fn} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left',
          marginBottom: 8, padding: '13px 14px', borderRadius: 12, background: T.card, border: `1px solid ${T.border2}`, color: T.text }}>
          <span style={{ fontSize: 22 }}>{icon}</span>
          <div style={{ flex: 1 }}><div style={{ fontSize: 14.5, fontWeight: 900 }}>{t}</div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>{s}</div></div>
          <span style={{ color: T.green, fontWeight: 900 }}>›</span>
        </button>))}
      <AlertCard />
      <MovesCard season={season} date={date} onOpen={onOpen} />
    </div>
  )
}

// Snipes: long shots (alt rungs, double/triple-doubles) the model prices well above the
// book. They lose most nights — graded on units over many shots, never on hit rate.
// Next man up — the Podziemski spot: stars sit, a rotation player inherits the touches,
// and the game stays close. Safe rung + stretch ladder per stat (fair odds, or the book's
// alt price when posted).
const NMU_STAT = { points: 'PTS', rebounds: 'REB', assists: 'AST', threes: '3PM' }
function NextManUp({ rows, onBreak, limit = 6 }) {
  const list = (rows || []).slice(0, limit)
  if (!list.length) return <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.45, padding: '6px 0' }}>
    No spots yet. These show up when a starter is ruled out and a teammate in a close game inherits the minutes.</div>
  const rung = (r, safe) => (
    <span key={r.n} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, fontSize: 11.5, fontFamily: T.mono,
      padding: '3px 7px', borderRadius: 8, border: `1px solid ${safe ? T.green : T.border}`,
      background: safe ? 'rgba(0,217,126,0.10)' : T.card2, color: safe ? T.green : T.text }}>
      <b>{r.n}+</b><span>{Math.round(r.prob * 100)}%</span>
      <span style={{ color: r.odds != null ? (r.ev > 0 ? T.green : T.muted2) : T.faint }}>
        {r.odds != null ? fmtOdds(r.odds) : `fair ${fmtOdds(r.fair)}`}</span>
    </span>)
  return (
    <div>
      {list.map((x, i) => (
        <div key={x.bdl_id} style={{ padding: '9px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
          <div onClick={() => {
            const [k, s] = Object.entries(x.stats).sort((a, b) => ((b[1].safe || b[1].stretch[0])?.prob || 0) - ((a[1].safe || a[1].stretch[0])?.prob || 0))[0]
            const r = s.safe || s.stretch[0]
            onBreak && onBreak({ ...x, kind: 'milestone', prop: k, threshold: r.n, prob: r.prob, proj: s.proj })
          }}
            style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}>
            <PAvatar url={x.headshot} team={x.team} size={34} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <b style={{ fontSize: 14, color: T.text }}>{x.name}</b>
                <span style={{ fontSize: 11, color: T.muted }}>{x.team} v {x.opp}</span>
              </div>
              <div style={{ fontSize: 11, color: T.muted2, marginTop: 1, lineHeight: 1.35 }}>
                <b style={{ color: T.green }}>+{x.bump}% usage</b>
                {x.out?.length ? <> · {x.out.map(o => `${o.name.split(' ').slice(-1)[0]}${o.status === 'out' ? '' : ` (${o.status[0].toUpperCase()})`}`).join(', ')} out</> : null}
                {' '}· {x.spread > 0 ? '+' : ''}{x.spread} · {Math.round(x.proj_minutes)} min</div>
            </div>
          </div>
          {Object.entries(x.stats).map(([k, s]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{ width: 62, fontSize: 11, fontWeight: 800, color: T.muted }}>{NMU_STAT[k]} <span style={{ color: T.faint, fontWeight: 600 }}>{s.proj}</span></span>
              {s.safe && rung(s.safe, true)}
              {s.stretch.map(r => rung(r, false))}
            </div>))}
        </div>))}
      <div style={{ fontSize: 10.5, color: T.faint, padding: '6px 0 2px', lineHeight: 1.4 }}>
        Green = safe rung (70%+). The rest are ladder rungs: long shots, small stakes. "Fair" is the price the model
        says is break-even; take a book's alt only when it pays more.</div>
    </div>
  )
}

// Lotto ladders — same-player REB + AST pairs on usage-bump players, priced together
// (the Dosunmu 8+/8+ & Holiday 8+/8+ $3 → $28k ticket). Long shots; tracked hits vs expected.
function LottoLadders({ lo, onBreak, limit = 8 }) {
  const rows = (lo?.plays || []).slice(0, limit)
  const rec = lo?.record
  const pair = (g, tone) => g && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, fontSize: 12 }}>
      <span style={{ width: 54, fontSize: 10, fontWeight: 800, letterSpacing: '.06em', color: tone }}>{tone === T.green ? 'STRETCH' : 'LOTTO'}</span>
      <b style={{ fontFamily: T.mono, color: T.text }}>{g.na}+ REB & {g.nb}+ AST</b>
      <span style={{ fontFamily: T.mono, color: tone }}>{(g.prob * 100).toFixed(1)}%</span>
      <span style={{ flex: 1 }} />
      <span style={{ fontFamily: T.mono, fontSize: 11, color: T.muted2 }}>fair {fmtOdds(g.fair)}</span>
      {g.alt_parlay != null && <span style={{ fontFamily: T.mono, fontSize: 11, color: g.ratio > 1 ? T.green : T.faint }}>
        alts {fmtOdds(g.alt_parlay)}</span>}
    </div>)
  return (
    <div>
      {!rows.length
        ? <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.45, padding: '6px 0' }}>
            None yet. These show when a teammate is out and a guard or wing picks up the ball and the minutes.</div>
        : rows.map((x, i) => (
          <div key={x.bdl_id} style={{ padding: '9px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
            <div onClick={() => onBreak && onBreak({ ...x, kind: 'milestone', prop: 'assists',
              threshold: (x.stretch || x.lotto).nb, prob: (x.stretch || x.lotto).prob })}
              style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}>
              <PAvatar url={x.headshot} team={x.team} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <b style={{ fontSize: 14, color: T.text }}>{x.name}</b>
                  <span style={{ fontSize: 11, color: T.muted }}>{x.team} v {x.opp}</span>
                </div>
                <div style={{ fontSize: 11, color: T.muted2, marginTop: 1 }}>
                  <b style={{ color: T.green }}>+{x.bump}% usage</b> · proj {x.proj.rebounds} reb / {x.proj.assists} ast · {Math.round(x.proj_minutes)} min
                  {x.out?.length ? <> · {x.out.slice(0, 2).map(o => o.name.split(' ').slice(-1)[0]).join(', ')} out</> : null}</div>
              </div>
            </div>
            {pair(x.stretch, T.green)}
            {pair(x.lotto, T.amber)}
          </div>))}
      <div style={{ fontSize: 10.5, color: T.faint, padding: '6px 0 2px', lineHeight: 1.4 }}>
        Both stats priced together (they rise together when the minutes do). Take a same-game parlay only when the book
        pays MORE than fair. $1–5 stakes: a lotto pair hits a few times a month.
        {rec?.graded ? <> Tracked: {rec.hits} hit of {rec.graded} (model expected {rec.expected_hits}).</> : null}</div>
    </div>
  )
}

function Snipes({ sn, onBreak }) {
  const plays = sn?.plays || []
  const rec = sn?.record
  return (
    <>
      <div>
        {!plays.length
          ? <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.45, padding: '6px 0' }}>
              No snipes. One fires only when a teammate is ruled out after the lines post and a long-shot
              rung (10+ assists, 30+ points, double-double) hasn't moved. Experimental — tiny stakes.</div>
          : plays.map((x, i) => (
            <div key={i} onClick={() => onBreak && onBreak({ ...x, kind: 'snipe' })} style={{ cursor: 'pointer',
              padding: '8px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <b style={{ fontSize: 14, color: T.text }}>{x.name}</b>
                <span style={{ fontSize: 11, color: T.muted }}>{x.team} v {x.opp}</span>
                <span style={{ flex: 1 }} />
                {x.result && <b style={{ fontSize: 11, color: x.result === 'win' ? T.green : T.muted }}>
                  {x.result === 'win' ? 'HIT' : x.result.toUpperCase()}{x.actual != null ? ` · ${x.actual}` : ''}</b>}
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.green, marginTop: 2 }}>
                {x.label.replace('assists', 'AST').replace('rebounds', 'REB').replace('points', 'PTS')}
                <span style={{ color: T.muted2, fontWeight: 700 }}> {fmtOdds(x.odds)}{x.book ? ` · ${bookName(x.book)}` : ''}</span></div>
              <div style={{ fontSize: 11.5, color: T.muted2, marginTop: 2, lineHeight: 1.4 }}>
                Model <b style={{ color: T.text }}>{(x.prob * 100).toFixed(1)}%</b> vs book {(x.book_prob * 100).toFixed(1)}%
                {' '}· EV +{Math.round(x.ev * 100)}%{x.why ? <> · {x.why}</> : null}</div>
            </div>))}
        <div style={{ fontSize: 10.5, color: T.faint, padding: '4px 0 2px' }}>
          {rec?.graded ? <>Snipes {rec.hits} hits of {rec.graded} (model expected {rec.expected_hits}) ·
            {' '}{rec.units >= 0 ? '+' : ''}{rec.units}u</> : <>Small stakes only — most of these miss. Tracked in units.</>}</div>
      </div>
    </>
  )
}

// News edges: availability news moved a projection after the book posted its line, and
// the line hasn't caught up. The only picks the board makes while the model is in research.
function NewsEdges({ news, onBreak }) {
  const sigs = news?.signals || []
  const rec = news?.record
  const settled = rec?.graded || 0
  return (
    <>
      <div>
        {!sigs.length
          ? <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.45, padding: '2px 0' }}>
              No news edges right now. One fires when injury or minutes news moves a player's projection
              after the line is posted and the book hasn't adjusted — checked every 15 minutes before tip.</div>
          : sigs.map((x, i) => (
            <div key={i} onClick={() => onBreak && onBreak({ ...x, kind: 'news' })} style={{ cursor: 'pointer',
              padding: '8px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <b style={{ fontSize: 14, color: T.text }}>{x.name}</b>
                <span style={{ fontSize: 11, color: T.muted }}>{x.team} v {x.opp}</span>
                <span style={{ flex: 1 }} />
                {x.result && <b style={{ fontSize: 11, color: x.result === 'win' ? T.green : x.result === 'loss' ? T.red : T.muted }}>
                  {x.result.toUpperCase()}{x.actual != null ? ` · ${x.actual}` : ''}</b>}
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.green, marginTop: 2 }}>
                {x.side === 'over' ? 'Over' : 'Under'} {x.line} {SHORT[x.prop] || x.prop}
                <span style={{ color: T.muted2, fontWeight: 700 }}> {fmtOdds(x.odds)}{x.book ? ` · ${bookName(x.book)}` : ''}</span></div>
              <div style={{ fontSize: 11.5, color: T.muted2, marginTop: 2, lineHeight: 1.4 }}>
                {x.news ? <>{x.news}. </> : null}Projection {x.open_mean?.toFixed(1)} → {x.mean?.toFixed(1)} since the line opened at {x.open_line};
                {' '}{Math.abs(x.shift).toFixed(1)} not priced in · {Math.round(x.prob * 100)}% · EV {x.ev >= 0 ? '+' : ''}{(x.ev * 100).toFixed(1)}%</div>
            </div>))}
        <div style={{ fontSize: 10.5, color: T.faint, marginTop: 6 }}>
          {settled ? <>News-edge record {rec.wins}-{rec.losses} · ROI {rec.roi >= 0 ? '+' : ''}{(rec.roi * 100).toFixed(1)}%
            {rec.clv_beat != null ? <> · market moved our way {Math.round(rec.clv_beat * 100)}%</> : null}</>
            : <>Tracked on its own record from day one.</>}</div>
      </div>
    </>
  )
}

const newsMeta = n => {
  const k = (n?.signals || []).length, r = n?.record
  const rec = r?.graded ? ` · record ${r.wins}-${r.losses}` : ''
  return k ? `${k} live — line hasn't caught up${rec}` : `None right now · checked every 15 min${rec}`
}
const snipeMeta = sn => {
  const k = (sn?.plays || []).length, r = sn?.record
  const rec = r?.graded ? ` · ${r.units >= 0 ? '+' : ''}${r.units}u` : ''
  return k ? `${k} live · experimental${rec}` : `None · fires only on late scratches${rec}`
}

// ── NFL-style pieces for Tonight ───────────────────────────────────────────────
function GameTicker({ games, sel, setSel }) {
  return (
    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '10px 0 4px', scrollbarWidth: 'none', margin: '0 -2px' }}>
      <button onClick={() => setSel(null)} style={{ flexShrink: 0, cursor: 'pointer', textAlign: 'left', borderRadius: 12, padding: '10px 14px',
        border: 'none', background: sel ? T.card : T.green, color: sel ? T.text : '#04140D' }}>
        <div style={{ fontSize: 15, fontWeight: 900 }}>All games</div>
        <div style={{ fontSize: 11.5, fontWeight: 700, opacity: 0.85 }}>{games.length} tonight</div>
      </button>
      {games.map(g => {
        const on = sel === g.game_id
        const t = tipTime(g.status), fin = /final/i.test(g.status || '')
        return (
          <button key={g.game_id} onClick={() => setSel(on ? null : g.game_id)} style={{ flexShrink: 0, cursor: 'pointer', borderRadius: 12,
            padding: '8px 12px', background: T.card, border: `1.5px solid ${on ? T.green : T.border2}`, color: T.text, textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Logo abbr={g.away} size={22} /><span style={{ fontSize: 10, color: T.faint }}>@</span><Logo abbr={g.home} size={22} /></div>
            <div style={{ fontSize: 11.5, fontWeight: 800, marginTop: 3 }}>{g.away} @ {g.home}</div>
            <div style={{ fontSize: 10.5, color: fin ? T.muted : T.green }}>{fin ? `Final ${g.away_score}-${g.home_score}` : (t || g.status || '')}</div>
          </button>)
      })}
    </div>
  )
}
function BoardStatus({ data, date, today }) {
  const games = data?.games || []
  const fin = games.filter(g => /final/i.test(g.status || '')).length
  const ck = checkedAt(data?.injuries_checked_at)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '9px 12px', borderRadius: 10,
      background: T.card, border: `1px solid ${T.border}` }}>
      <span style={{ width: 8, height: 8, borderRadius: 4, background: T.green }} />
      <b style={{ fontSize: 13, color: T.text }}>{date === today ? "Tonight's board" : 'Board'}</b>
      <span style={{ fontSize: 12, color: T.muted }}>· {games.length - fin} to play{fin ? ` · ${fin} final` : ''}</span>
      <span style={{ flex: 1 }} />
      {ck && <span style={{ fontSize: 11, color: T.faint }}>injuries {ck}</span>}
    </div>
  )
}
function CtaCard({ icon, title, sub, onClick, primary }) {
  return (
    <button onClick={onClick} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left',
      marginTop: 12, padding: '14px 15px', borderRadius: 14, color: T.text,
      background: primary ? 'linear-gradient(120deg,#0c2018,#0a1119)' : T.card,
      border: `1.5px solid ${primary ? T.green : T.border2}` }}>
      <span style={{ fontSize: 26, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 17, fontWeight: 900 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: T.muted2, marginTop: 2, lineHeight: 1.4 }}>{sub}</div>
      </div>
      {primary ? <span style={{ background: T.green, color: '#04140D', fontWeight: 900, borderRadius: 9, padding: '9px 14px' }}>Go</span>
        : <span style={{ color: T.green, fontSize: 18, fontWeight: 900 }}>›</span>}
    </button>
  )
}
function HeadCard({ icon, title, sub, onMore, children }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border2}`, borderRadius: 14, padding: '11px 10px', minWidth: 0 }}>
      <div onClick={onMore} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', cursor: onMore ? 'pointer' : 'default' }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 900, color: T.text, lineHeight: 1.2 }}>{title}</div>
          <div style={{ fontSize: 10.5, color: T.muted, marginTop: 2 }}>{sub}</div>
        </div>
        {onMore && <span style={{ color: T.green, fontWeight: 900 }}>›</span>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>{children}</div>
    </div>
  )
}
function MiniRow({ team, top, sub, right, rightColor, onClick, shot }) {
  return (
    <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 7, background: T.card2, border: `1px solid ${T.border}`,
      borderRadius: 10, padding: '7px 8px', cursor: onClick ? 'pointer' : 'default' }}>
      {team && (shot !== undefined ? <PAvatar url={shot} team={team} size={30} /> : <Logo abbr={team} size={20} />)}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{top}</div>
        <div style={{ fontSize: 10.5, color: T.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>
      </div>
      <b style={{ fontFamily: T.mono, fontSize: 13, color: rightColor || T.green }}>{right}</b>
    </div>
  )
}
const shortName = n => { const p = (n || '').split(' '); return p.length > 1 ? `${p[0][0]}. ${p.slice(1).join(' ')}` : n }

function HomeTab({ data, loading, season, date, today, onOpen, onBreak, go, onBuild, homeSel }) {
  const recSt = useRecordStatus()
  const [more, setMore] = useState(false)
  if (loading) return <Spinner />
  const top = data?.top || []
  const markets = Object.values(data?.by_market || {})
  const boosts = data?.boosts || []
  const outs = (data?.injuries || []).filter(i => i.status === 'out' || i.status === 'doubtful')
  const news = data?.news, sn = data?.snipes
  const [sel, setSel] = [homeSel.v, homeSel.set]
  const games = data?.games || []
  const inGame = x => !sel || x.game_id === sel
  const ms = (data?.milestones?.plays || []).filter(inGame)
  const bumps = [...(data?.milestones?.plays || [])].filter(x => inGame(x) && (x.bump || 0) >= 3).sort((a, b) => b.bump - a.bump)
  const byTot = [...games].filter(g => g.total != null).sort((a, b) => b.total - a.total)
  const sigs = (news?.signals || []), snp = (sn?.plays || [])
  return (
    <div>
      {games.length > 0 && <GameTicker games={games} sel={sel} setSel={setSel} />}
      <BoardStatus data={data} date={date} today={today} />
      {data?.phase !== 'live' ? <Empty message="No plays posted for this date yet — the board builds each morning." /> : <>
        <CtaCard primary icon="🧪" title="Generate a play" sub="Pick the games + a stat — get the highest-probability plays, ranked." onClick={() => go('lab')} />
        <div style={{ marginTop: 12, padding: '12px 13px', borderRadius: 14, background: T.card, border: `1px solid ${T.border2}` }}>
          <div style={{ fontSize: 15, fontWeight: 900, color: T.text }}>🔎 Build it yourself <span style={{ fontSize: 12, fontWeight: 600, color: T.muted }}>· what are you looking for?</span></div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            {[['points', 'Points'], ['rebounds', 'Rebounds'], ['assists', 'Assists'], ['threes', 'Threes'], ['pra', 'PRA'], ['', 'All reads'], ['games', 'By game']].map(([k, l]) => (
              <button key={l} onClick={() => k === 'games' ? go('games') : onBuild(k)} style={{ cursor: 'pointer', fontSize: 14, fontWeight: 800,
                padding: '9px 14px', borderRadius: 10, background: T.card2, border: `1px solid ${T.border2}`, color: T.text }}>{l}</button>))}
          </div>
        </div>

        <SlipCheckButton season={season} date={date} />
        {(() => {
          const favs = readFavs()
          const mine = (data?.milestones?.plays || []).filter(x => favs.includes(x.bdl_id))
          return favs.length > 0 && <div style={{ marginTop: 12, padding: '11px 12px', borderRadius: 14, background: T.card, border: `1px solid ${T.border2}` }}>
            <div style={{ fontSize: 14.5, fontWeight: 900, color: T.text }}>★ Your players <span style={{ fontSize: 11.5, color: T.muted, fontWeight: 600 }}>· tonight</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {mine.length ? mine.map((x, i) => <MiniRow key={i} team={x.team} shot={x.headshot || null} top={x.name}
                sub={`${x.threshold}+ ${SHORT[x.prop]} · vs ${x.opp}`} right={`${Math.round(x.prob * 100)}%`} onClick={() => onBreak({ ...x, kind: 'milestone', date })} />)
                : <div style={{ fontSize: 11.5, color: T.muted }}>None of your starred players has a built-to-hit read tonight.</div>}
            </div>
          </div>
        })()}
        <AskBrkr season={season} date={date} onBreak={onBreak} />
        <div style={{ display: 'flex', alignItems: 'baseline', margin: '18px 2px 8px' }}>
          <span style={{ fontSize: 13, fontWeight: 900, color: T.muted, letterSpacing: '.08em' }}>{date === today ? "TONIGHT'S HEADLINES" : 'HEADLINES'}</span>
          <span style={{ flex: 1 }} /><span style={{ fontSize: 11, color: T.faint }}>tap a game up top for just that game</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <HeadCard icon="🎯" title="Highest-probability plays" sub={data?.milestones?.record?.graded
            ? `Built to hit · ${Math.round(data.milestones.record.hit_rate * 100)}% so far` : 'Built to hit'} onMore={() => onBuild('')}>
            {ms.slice(0, 3).map((x, i) => <MiniRow key={i} team={x.team} shot={x.headshot || null} top={shortName(x.name)}
              sub={`${x.threshold}+ ${SHORT[x.prop] || x.prop}`} right={`${Math.round(x.prob * 100)}%`} onClick={() => onBreak({ ...x, kind: 'milestone', date })} />)}
            {!ms.length && <div style={{ fontSize: 11, color: T.muted }}>None for this game.</div>}
          </HeadCard>
          <HeadCard icon="🚑" title="Teammates out" sub="Minutes boosts tonight" onMore={() => onBuild('', 'out')}>
            {bumps.slice(0, 3).map((x, i) => <MiniRow key={i} team={x.team} shot={x.headshot || null} top={shortName(x.name)}
              sub={`vs ${x.opp} · ${x.threshold}+ ${SHORT[x.prop]}`} right={`+${x.bump}%`} rightColor={T.amber}
              onClick={() => onBreak({ ...x, kind: 'milestone', date })} />)}
            {!bumps.length && <div style={{ fontSize: 11, color: T.muted }}>No big minutes boosts.</div>}
          </HeadCard>
          <HeadCard icon="🔥" title="Most action" sub="Highest totals — most points expected" onMore={() => go('games')}>
            {byTot.slice(0, 2).map((g, i) => <MiniRow key={i} team={g.away} top={`${g.away} @ ${g.home}`} sub={tipTime(g.status) || ''}
              right={g.total} rightColor={T.text} onClick={() => setSel(g.game_id)} />)}
          </HeadCard>
          <HeadCard icon="🧊" title="Least action" sub="Lowest totals — fewest points expected" onMore={() => go('games')}>
            {byTot.slice(-2).reverse().map((g, i) => <MiniRow key={i} team={g.away} top={`${g.away} @ ${g.home}`} sub={tipTime(g.status) || ''}
              right={g.total} rightColor={T.text} onClick={() => setSel(g.game_id)} />)}
          </HeadCard>
          <HeadCard icon="⚡" title="News edges" sub={newsMeta(news)}>
            {sigs.slice(0, 2).map((x, i) => <MiniRow key={i} team={x.team} shot={x.headshot || null} top={shortName(x.name)}
              sub={`${x.side === 'over' ? 'O' : 'U'} ${x.line} ${SHORT[x.prop]}`} right={fmtOdds(x.odds)} onClick={() => onBreak({ ...x, kind: 'news' })} />)}
            {!sigs.length && <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.4 }}>Fires when injury news beats the line.</div>}
          </HeadCard>
          <HeadCard icon="🔫" title="Snipes" sub="Late-news long shots">
            {snp.slice(0, 2).map((x, i) => <MiniRow key={i} team={x.team} shot={x.headshot || null} top={shortName(x.name)} sub={x.label}
              right={fmtOdds(x.odds)} onClick={() => onBreak({ ...x, kind: 'snipe' })} />)}
            {!snp.length && <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.4 }}>Only on late scratches.</div>}
          </HeadCard>
        </div>
        {(data?.next_man_up || []).length > 0 && <Section icon="🔁" title="Next man up"
          meta={`${data.next_man_up.length} usage spots · starters out, close games`} metaColor={T.green} defaultOpen>
          <NextManUp rows={data.next_man_up.filter(x => !sel || x.game_id === sel)} onBreak={onBreak} limit={4} />
        </Section>}
        {(data?.lotto?.plays || []).length > 0 && <Section icon="🎟️" title="Lotto ladders"
          meta={`${data.lotto.plays.length} REB+AST pairs · usage spots · tiny stakes`}>
          <LottoLadders lo={{ ...data.lotto, plays: data.lotto.plays.filter(x => !sel || x.game_id === sel) }} onBreak={onBreak} limit={5} />
        </Section>}
        {(data?.soft_spots || []).length > 0 && (() => {
          const sps = data.soft_spots.filter(x => !sel || x.game_id === sel)
          return <div style={{ marginTop: 10, background: T.card, border: `1px solid ${T.border2}`, borderRadius: 14, padding: '11px 12px 4px' }}>
            <div onClick={() => go('matchups')} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>🎯</span>
              <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 900 }}>Soft spots · where defenses break</div>
                <div style={{ fontSize: 10.5, color: T.muted }}>The stat × position each defense gives up most — and who attacks it</div></div>
              <span style={{ color: T.green, fontWeight: 900 }}>›</span>
            </div>
            {sps.length ? sps.slice(0, 3).map((sp, i) => <SpotRow key={i} sp={sp} onBreak={onBreak} date={date} compact />)
              : <div style={{ fontSize: 11.5, color: T.muted, padding: '8px 0' }}>No standout soft spots for this game.</div>}
          </div>
        })()}
        {outs.length > 0 && <Section icon="🚑" title={`Injury report · ${outs.length} out`}
          meta="Minutes already redistributed on the board" right="All injuries ›" onRight={() => go('injuries')}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 8 }}>
            {outs.slice(0, 16).map((i, k) => (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: T.text,
                background: T.card2, border: `1px solid ${T.border}`, borderRadius: 14, padding: '3px 8px 3px 4px' }}>
                <Logo abbr={i.team} size={16} />{i.name}
              </span>))}
          </div>
        </Section>}
      </>}

      <div style={{ marginTop: 12 }}>
        {recSt?.public || showRecordOverride()
          ? <TrustBar rec={recSt} goRecord={() => go('record')} />
          : <div style={{ fontSize: 11.5, color: T.muted, padding: '2px 4px' }}>
              📊 Record: testing — opens after {recSt?.nights_needed ?? 7} graded nights
              {' '}({recSt?.nights_graded ?? 0} so far).</div>}
      </div>

      {data?.phase === 'live' && <>
        <button onClick={() => setMore(m => !m)} aria-expanded={more} style={{ display: 'flex', width: '100%', alignItems: 'center',
          justifyContent: 'space-between', gap: 10, cursor: 'pointer', textAlign: 'left', color: T.text, background: T.card,
          border: `1px solid ${T.border2}`, borderRadius: 12, padding: '12px 14px', margin: '12px 0 4px' }}>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 900 }}>{more ? 'Less' : 'More tonight (research)'}</span>
            {!more && <span style={{ display: 'block', fontSize: 11, color: T.muted, marginTop: 2, lineHeight: 1.4 }}>
              Model reads vs the book · biggest gaps · best by market · minutes boosts · line moves</span>}
          </span>
          <span style={{ color: T.green, fontWeight: 900, fontSize: 18, flexShrink: 0 }}>{more ? '▴' : '▾'}</span>
        </button>
        {more && <>
          <ResearchBanner />
          <SectionLabel right="Top Plays ›" onRight={() => go('top')}>Strongest model reads</SectionLabel>
          <Card style={{ padding: '2px 12px' }}>
            {((data?.best || []).length ? data.best.slice(0, 6) : (data?.leans || []))
              .map((p, i) => <PlayRow key={i} p={p} onOpen={onOpen} first={i === 0} />)}
          </Card>
          {top.length > 0 && <>
            <SectionLabel right="Full board ›" onRight={() => go('board')}>Biggest model-vs-market gaps</SectionLabel>
            <Card style={{ padding: '2px 12px' }}>
              {top.map((p, i) => <PlayRow key={i} p={p} onOpen={onOpen} first={i === 0} />)}
            </Card>
          </>}
          {markets.length > 0 && <>
            <SectionLabel>Best by market</SectionLabel>
            <ScrollRow style={{ gap: 8 }}>
              {markets.map((p, i) => <MarketCard key={i} p={p} onOpen={onOpen} />)}
            </ScrollRow>
          </>}
          {boosts.length > 0 && <>
            <SectionLabel>Minutes boosts · teammates out</SectionLabel>
            <Card style={{ padding: '2px 12px' }}>
              {boosts.slice(0, 5).map((p, i) => <PlayRow key={i} p={p} onOpen={onOpen} first={i === 0} />)}
            </Card>
          </>}
          <MovesCard season={season} date={date} onOpen={onOpen} />
        </>}
      </>}
    </div>
  )
}

// ── Top Plays (every game's top plays, ranked — mirrors NFL Top Plays) ───────
function playLine(p) {
  return `${p.name} · ${p.side === 'under' ? 'U' : 'O'} ${p.side_line ?? p.line} ${SHORT[p.prop] || p.market} · ${tierLabel(p.tier)} · ${pctInt(p.side_prob)}` +
    (p.edge_pct != null && !p.line_suspect && Math.abs(p.edge_pct) >= 1 ? ` · ${Math.abs(p.edge_pct)}pp edge` : '')
}
const gameText = (g, n) => [`${g.away} @ ${g.home}`, ...g.plays.slice(0, n).map((p, i) => `${i + 1}. ${playLine(p)}`)].join('\n')

function CopyBtn({ text, label = 'Copy', small }) {
  const [done, setDone] = useState(false)
  const copy = () => {
    const ok = () => { setDone(true); setTimeout(() => setDone(false), 1500) }
    try {
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(ok, ok)
      else { const t = document.createElement('textarea'); t.value = text; document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove(); ok() }
    } catch (_) { ok() }
  }
  return (
    <button onClick={copy} style={{ cursor: 'pointer', fontFamily: T.mono, fontWeight: 700, fontSize: small ? 10.5 : 12,
      padding: small ? '4px 9px' : '7px 12px', borderRadius: 8, color: done ? '#04140D' : T.green,
      background: done ? T.green : T.greenDim, border: `1px solid ${T.green}${done ? '' : '55'}`, whiteSpace: 'nowrap' }}>
      {done ? '✓ Copied' : `⧉ ${label}`}
    </button>
  )
}

function TopPlaysTab({ season, date, onOpen, onBreak }) {
  const [d, setD] = useState(null)
  const [prop, setProp] = useState('')
  const [recoOnly, setRecoOnly] = useState(false)
  useEffect(() => {
    let live = true
    setD(null)
    getNBASlate(season, date, 25).then(r => { if (live) setD(r) })
    return () => { live = false }
  }, [season, date])
  if (!d) return <Spinner />
  const N = 5
  const games = (d.games || []).map(g => ({ ...g, plays: (g.plays || [])
    .filter(p => (prop ? p.prop === prop : p.prop !== 'threes') && (!recoOnly || ['ELITE', 'BEST BET', 'STRONG'].includes(p.tier)))
    .sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier) || b.side_prob - a.side_prob) }))
    .filter(g => g.plays.length)
  const allText = games.map(g => gameText(g, N)).join('\n\n')
  return (
    <div>
      <Section icon="🎯" title="Built to hit" meta={msMeta(d.milestones)} metaColor={T.green} defaultOpen>
        <Milestones ms={d.milestones} limit={10} onBreak={onBreak} date={date} />
      </Section>
      <Section icon="🔁" title="Next man up" meta={`${(d.next_man_up || []).length} usage spots · starters out, close games`}
        defaultOpen={(d.next_man_up || []).length > 0} empty={!(d.next_man_up || []).length}>
        <NextManUp rows={d.next_man_up} onBreak={onBreak} limit={12} />
      </Section>
      <Section icon="🎟️" title="Lotto ladders" meta={`${(d.lotto?.plays || []).length} REB+AST pairs · long shots, tracked`}
        empty={!(d.lotto?.plays || []).length}>
        <LottoLadders lo={d.lotto} onBreak={onBreak} limit={14} />
      </Section>
      <Section icon="⚡" title="News edges" meta={newsMeta(d.news)} defaultOpen={(d.news?.signals || []).length > 0}
        empty={!(d.news?.signals || []).length}><NewsEdges news={d.news} onBreak={onBreak} /></Section>
      <Section icon="🔫" title="Snipes" meta={snipeMeta(d.snipes)} defaultOpen={(d.snipes?.plays || []).length > 0}
        empty={!(d.snipes?.plays || []).length}><Snipes sn={d.snipes} onBreak={onBreak} /></Section>
      <Section icon="🧪" title="Model reads by game (research)"
        meta={`${games.length} games · top ${N} each · tagged WATCH until proven`}>
        <ResearchBanner />

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '10px 0 6px', scrollbarWidth: 'none' }}>
        {PROPS.map(([k, label]) => <Chip key={k} on={prop === k} onClick={() => setProp(k)}>{label}</Chip>)}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 10px' }}>
        <button onClick={() => setRecoOnly(v => !v)} style={{ cursor: 'pointer', fontSize: 11.5, fontWeight: 800,
          padding: '5px 10px', borderRadius: 7, border: `1px solid ${recoOnly ? G2 : T.border}`,
          background: recoOnly ? G2 : T.card2, color: recoOnly ? '#04140D' : T.muted }}>Strong+ only</button>
        <span style={{ flex: 1, fontSize: 11, color: T.muted }}>Top {N} per game · best rating, then hit %</span>
        {games.length > 0 && <CopyBtn text={allText} label="Copy all" />}
      </div>
      {!games.length ? <Empty message={prop || recoOnly ? 'No plays match these filters.' : 'No plays for this date yet.'} />
        : games.map(g => (
          <Card key={g.game_id} style={{ marginBottom: 10, padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Logo abbr={g.away} size={22} /><span style={{ fontSize: 14, fontWeight: 800 }}>{g.away}</span>
              <span style={{ color: T.faint, fontSize: 12 }}>@</span>
              <Logo abbr={g.home} size={22} /><span style={{ fontSize: 14, fontWeight: 800 }}>{g.home}</span>
              <span style={{ flex: 1, fontSize: 10.5, color: T.muted, fontFamily: T.mono, textAlign: 'right' }}>
                {[tipTime(g.status), spreadTxt(g), g.total != null ? `O/U ${g.total}` : null].filter(Boolean).join(' · ')}</span>
              <CopyBtn text={gameText(g, N)} small />
            </div>
            {g.out?.length > 0 && <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 2 }}><b style={{ color: T.red }}>Out:</b> {g.out.join(', ')}</div>}
            {g.plays.slice(0, N).map((p, i) => <PlayRow key={i} p={p} onOpen={onOpen} first={i === 0} />)}
          </Card>
        ))}
      </Section>
      <div style={{ fontSize: 10, color: T.faint, marginTop: 6, lineHeight: 1.5 }}>
        Ratings use the same rules as NFL: a Strong+ play needs confidence, probability, and — when priced — a real edge
        over the book. Research, not betting advice · 21+.
      </div>
    </div>
  )
}

// ── Alerts (push when a player is ruled OUT + who gains the minutes) ──────────
function AlertCard() {
  const [st, setSt] = useState(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => { getNBAPushState().then(setSt) }, [])
  if (!st || !st.supported || !st.enabled) return null
  const on = st.subscribed
  const toggle = async () => {
    setBusy(true)
    if (on) await unsubscribeNBAPush()
    else {
      const r = await subscribeNBAPush([])
      if (!r.ok && r.reason === 'denied') alert('Notifications are blocked for this site in your browser settings.')
    }
    setSt(await getNBAPushState()); setBusy(false)
  }
  return (
    <Card style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
      <span style={{ fontSize: 20 }}>{on ? '🔔' : '🔕'}</span>
      <div style={{ flex: 1, fontSize: 12.5, color: T.muted2, lineHeight: 1.35 }}>
        <b style={{ color: T.text }}>OUT alerts</b> — a push the moment a player is ruled out, with who gains his minutes.
        {on && st.teams?.length ? <div style={{ fontSize: 11, color: T.faint }}>Following: {st.teams.join(', ')}</div> : null}
      </div>
      <button disabled={busy} onClick={toggle} style={{ cursor: 'pointer', fontSize: 12, fontWeight: 800, padding: '7px 12px', borderRadius: 8,
        border: `1px solid ${on ? T.border2 : T.green}`, background: on ? T.card2 : T.green, color: on ? T.muted : '#04140D' }}>
        {busy ? '…' : on ? 'Turn off' : 'Turn on'}</button>
    </Card>
  )
}

// ── Line movement (open → now; did the market move toward the model?) ──────────
function MovesCard({ season, date, onOpen, limit = 6 }) {
  const [d, setD] = useState(null)
  useEffect(() => { let a = true; getNBAMoves(season, date, 40).then(r => { if (a) setD(r) }); return () => { a = false } }, [season, date])
  const mv = d?.moves || []
  if (!mv.length) return null
  return <>
    <SectionLabel>📈 Line moves · {d.toward_model}/{d.moved} toward the model</SectionLabel>
    <Card style={{ padding: '2px 12px' }}>
      {mv.slice(0, limit).map((p, i) => (
        <div key={i} onClick={() => onOpen(p)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', cursor: 'pointer',
          borderTop: i ? `1px solid ${T.border}` : 'none' }}>
          <Logo abbr={p.team} size={22} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
            <div style={{ fontSize: 10.5, color: T.muted }}>{p.market} · model {p.side === 'under' ? 'U' : 'O'}</div>
          </div>
          <span style={{ fontFamily: T.mono, fontSize: 12, color: T.muted2 }}>
            {p.line_move ? `${p.open_line} → ${p.book_line}` : `${p.book_line} · ${p.fair_move > 0 ? '+' : ''}${p.fair_move}pp O`}</span>
          <span style={{ fontSize: 9.5, fontWeight: 800, borderRadius: 4, padding: '2px 5px', minWidth: 52, textAlign: 'center',
            color: p.agrees ? '#04140D' : T.text, background: p.agrees ? G2 : T.card2, border: p.agrees ? 'none' : `1px solid ${T.border2}` }}>
            {p.agrees ? 'WITH US' : 'AGAINST'}</span>
        </div>))}
    </Card>
  </>
}

// ── Parlay builder ───────────────────────────────────────────────────────────
// Same-player legs move together (points ↔ PRA strongly, points ↔ threes, etc.);
// teammates compete for the same shots; same-game legs share pace. Pairwise ρ on the
// leg outcomes, flipped when one leg is an under — then a pairwise-corrected product.
const SAME_PLAYER_RHO = { 'points|pra': 0.8, 'pra|rebounds': 0.5, 'assists|pra': 0.5, 'points|threes': 0.6,
  'pra|threes': 0.45, 'assists|points': 0.25, 'points|rebounds': 0.2, 'assists|rebounds': 0.15,
  'assists|threes': 0.15, 'rebounds|threes': 0.05 }
function legRho(a, b) {
  let r = 0
  if (a.bdl_id === b.bdl_id) r = SAME_PLAYER_RHO[[a.prop, b.prop].sort().join('|')] ?? 0.2
  else if (a.team === b.team) r = (a.prop === b.prop && (a.prop === 'points' || a.prop === 'assists')) ? -0.08 : 0.03
  else if (a.game_id && a.game_id === b.game_id) r = 0.05
  const sa = a.side === 'under' ? -1 : 1, sb = b.side === 'under' ? -1 : 1
  return r * sa * sb
}
export function parlayProb(legs) {
  if (!legs.length) return 0
  let p = legs.reduce((x, l) => x * l.side_prob, 1)
  for (let i = 0; i < legs.length; i++) for (let j = i + 1; j < legs.length; j++) {
    const a = legs[i].side_prob, b = legs[j].side_prob
    p *= Math.max(0.2, 1 + legRho(legs[i], legs[j]) * Math.sqrt(((1 - a) * (1 - b)) / (a * b)))
  }
  return Math.min(Math.max(p, 0), Math.min(...legs.map(l => l.side_prob)))
}
const toAmerican = p => p <= 0 || p >= 1 ? '—' : p >= 0.5 ? `${Math.round(-100 * p / (1 - p))}` : `+${Math.round(100 * (1 - p) / p)}`
const fromAmerican = s => { const o = parseFloat(String(s).replace('+', '')); return isNaN(o) || o === 0 ? null : o > 0 ? 1 + o / 100 : 1 + 100 / -o }

function ParlaySheet({ legs, setLegs, onClose }) {
  const [price, setPrice] = useState('')
  const p = parlayProb(legs)
  const indep = legs.reduce((x, l) => x * l.side_prob, 1)
  const dec = fromAmerican(price)
  const ev = dec ? p * dec - 1 : null
  const text = legs.map(l => `${l.name} ${l.side === 'under' ? 'U' : 'O'}${l.side_line ?? l.line} ${SHORT[l.prop] || l.market}`).join('\n')
    + `\nModel: ${(p * 100).toFixed(1)}% (fair ${toAmerican(p)})`
  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: '10px 16px 26px' }}>
        <div style={{ fontSize: 17, fontWeight: 900, color: T.text }}>Parlay · {legs.length} leg{legs.length === 1 ? '' : 's'}</div>
        <Card style={{ marginTop: 10, padding: '2px 12px' }}>
          {legs.map((l, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <Logo abbr={l.team} size={22} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{l.name}</div>
                <div style={{ marginTop: 2 }}><SidePill p={l} /></div>
              </div>
              <span style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 800, color: T.green }}>{pctInt(l.side_prob)}</span>
              <button onClick={() => setLegs(legs.filter((_, j) => j !== i))} aria-label="Remove leg"
                style={{ border: 'none', background: 'none', color: T.muted, fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>))}
        </Card>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
          <Card style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: T.mono, fontSize: 22, fontWeight: 900, color: T.text }}>{(p * 100).toFixed(1)}%</div>
            <div style={{ fontSize: 9.5, color: T.muted, letterSpacing: '.08em' }}>MODEL CHANCE</div>
          </Card>
          <Card style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: T.mono, fontSize: 22, fontWeight: 900, color: T.text }}>{toAmerican(p)}</div>
            <div style={{ fontSize: 9.5, color: T.muted, letterSpacing: '.08em' }}>FAIR ODDS</div>
          </Card>
        </div>
        {legs.length > 1 && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 8, lineHeight: 1.45 }}>
          {Math.abs(p - indep) / indep > 0.03
            ? <>Legs that move together change the math: {(indep * 100).toFixed(1)}% if they were independent → {(p * 100).toFixed(1)}% accounting for correlation.</>
            : <>These legs are close to independent.</>}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <span style={{ fontSize: 12.5, color: T.muted2 }}>Book's price</span>
          <input value={price} onChange={e => setPrice(e.target.value)} placeholder="+450" inputMode="numeric"
            style={{ width: 90, padding: '7px 9px', borderRadius: 8, background: T.card, color: T.text, border: `1px solid ${T.border2}`, fontFamily: T.mono }} />
          {ev != null && <span style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 800, color: ev >= 0 ? T.green : T.red }}>
            {ev >= 0 ? '+' : ''}{(ev * 100).toFixed(1)}% EV</span>}
        </div>
        {ev != null && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 6 }}>
          Per $100: {ev >= 0 ? 'about +' : 'about −'}${Math.abs(ev * 100).toFixed(0)} expected, by the model. {ev < 0 ? 'The book is charging more than the model thinks it\'s worth.' : ''}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={() => { try { navigator.clipboard.writeText(text) } catch (_) {} }} style={{ flex: 1, cursor: 'pointer', padding: '9px', borderRadius: 8,
            border: `1px solid ${T.green}55`, background: T.greenDim, color: T.green, fontWeight: 800 }}>⧉ Copy</button>
          <button onClick={() => { setLegs([]); onClose() }} style={{ flex: 1, cursor: 'pointer', padding: '9px', borderRadius: 8,
            border: `1px solid ${T.border2}`, background: T.card2, color: T.muted, fontWeight: 800 }}>Clear</button>
        </div>
        <div style={{ fontSize: 10.5, color: T.faint, marginTop: 14, lineHeight: 1.5 }}>
          Correlation is an approximation (same-player and same-team effects). Research, not betting advice · 21+.</div>
      </div>
    </Sheet>
  )
}

// ── Board ─────────────────────────────────────────────────────────────────────
function BoardTab({ season, date, onOpen }) {
  const research = useResearch()
  const [prop, setProp] = useState('')
  const [sort, setSort] = useState('edge')
  const [valueOnly, setValueOnly] = useState(false)
  const [q, setQ] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let live = true
    setLoading(true)
    getNBALeaderboard(season, date, prop, 200, sort).then(d => { if (live) { setData(d); setLoading(false) } })
    return () => { live = false }
  }, [season, date, prop, sort])
  const plays = (data?.plays || []).filter(p => !valueOnly || p.value).filter(searchMatch(q, p => [p.name, p.team, p.opp]))
  return (
    <div>
      <div style={{ marginTop: 10 }}><SearchBar value={q} onChange={setQ} placeholder="Search player or team…" /></div>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, scrollbarWidth: 'none' }}>
        {PROPS.map(([k, label]) => <Chip key={k} on={prop === k} onClick={() => setProp(k)}>{label}</Chip>)}
      </div>
      <div style={{ display: 'flex', gap: 6, margin: '6px 0 10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: T.faint }}>Sort</span>
        {[['edge', 'Edge'], ['prob', 'Model %'], ['proj', 'Projection']].map(([k, label]) => (
          <button key={k} onClick={() => setSort(k)} style={{ cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
            padding: '5px 10px', borderRadius: 7, border: `1px solid ${sort === k ? G2 : T.border}`,
            background: sort === k ? 'rgba(47,227,122,0.12)' : T.card2, color: sort === k ? G2 : T.muted }}>{label}</button>
        ))}
        {!research?.research_mode && <button onClick={() => setValueOnly(v => !v)} style={{ marginLeft: 'auto', cursor: 'pointer', fontSize: 11.5, fontWeight: 800,
          padding: '5px 10px', borderRadius: 7, border: `1px solid ${valueOnly ? G2 : T.border}`,
          background: valueOnly ? G2 : T.card2, color: valueOnly ? '#04140D' : T.muted }}>VALUE only</button>}
      </div>
      {loading ? <Spinner /> : plays.length === 0
        ? <Empty message={q || valueOnly ? 'Nothing matches those filters.' : 'No plays posted for this date yet.'} />
        : <Card style={{ padding: '2px 12px' }}>
            {plays.map((p, i) => <PlayRow key={i} p={p} onOpen={onOpen} first={i === 0} />)}
          </Card>}
    </div>
  )
}

// ── Games ─────────────────────────────────────────────────────────────────────
function GameCard({ g, onOpen }) {
  const tip = tipTime(g.status)
  const final = isFinal(g.status) && g.home_score != null
  const side = (abbr, full, score) => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <Logo abbr={abbr} size={40} />
      <div style={{ fontSize: 12, fontWeight: 800, color: T.text, textAlign: 'center' }}>{full || abbr}</div>
      {final && <div style={{ fontFamily: T.mono, fontSize: 20, fontWeight: 900, color: T.text }}>{score}</div>}
    </div>
  )
  return (
    <Card style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {side(g.away, g.away_full, g.away_score)}
        <div style={{ textAlign: 'center', minWidth: 90 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: final ? T.muted : ORANGE }}>{final ? 'FINAL' : (tip || g.status || 'Scheduled')}</div>
          {spreadTxt(g) && <div style={{ fontSize: 11.5, color: T.muted2, marginTop: 4, fontFamily: T.mono }}>{spreadTxt(g)}</div>}
          {g.total != null && <div style={{ fontSize: 11.5, color: T.muted2, fontFamily: T.mono }}>O/U {g.total}</div>}
        </div>
        {side(g.home, g.home_full, g.home_score)}
      </div>
      {g.out?.length > 0 && <div style={{ marginTop: 10, fontSize: 11.5, color: T.muted, lineHeight: 1.45 }}>
        <b style={{ color: T.red }}>Out:</b> {g.out.join(', ')}</div>}
      {g.plays?.length > 0 && <div style={{ marginTop: 6, borderTop: `1px solid ${T.border}` }}>
        {g.plays.map((p, i) => <PlayRow key={i} p={p} onOpen={onOpen} first={i === 0} />)}
      </div>}
      <div style={{ marginTop: 6, fontSize: 10.5, color: T.faint }}>
        {g.props} props modeled{g.value ? ` · ${g.value} value` : ''}</div>
    </Card>
  )
}

function GamesTab({ data, loading, onOpen }) {
  if (loading) return <Spinner />
  const games = data?.games || []
  if (!games.length) return <Empty message="No games on this date." />
  return <div>{games.map((g, i) => <GameCard key={i} g={g} onOpen={onOpen} />)}</div>
}

// ── Injuries ──────────────────────────────────────────────────────────────────
const STATUS_COLOR = { out: T.red, doubtful: T.orange, questionable: T.amber, probable: T.mint }
function InjuriesTab({ data, loading }) {
  const [f, setF] = useState('')
  const [q, setQ] = useState('')
  if (loading) return <Spinner />
  const rows = (data?.injuries || []).filter(i => !f || i.status === f).filter(searchMatch(q, i => [i.name, i.team]))
  const byTeam = {}
  rows.forEach(i => { (byTeam[i.team] = byTeam[i.team] || []).push(i) })
  return (
    <div>
      <AlertCard />
      {checkedAt(data?.injuries_checked_at) && <div style={{ fontSize: 11.5, fontWeight: 700, color: G2, marginTop: 10 }}>
        ● Report checked {checkedAt(data.injuries_checked_at)} · re-checked every 15 min until tip</div>}
      <div style={{ fontSize: 12, color: T.muted, margin: '10px 0 8px', lineHeight: 1.45 }}>
        Teams on this slate only. OUT / Doubtful players are removed from the board and their minutes
        flow to teammates; Questionable plays carry a Q badge.
      </div>
      <SearchBar value={q} onChange={setQ} placeholder="Search player or team…" />
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        {[['', 'All'], ['out', 'Out'], ['doubtful', 'Doubtful'], ['questionable', 'Questionable']].map(([k, l]) =>
          <Chip key={k} on={f === k} onClick={() => setF(k)}>{l}</Chip>)}
      </div>
      {!rows.length ? <Empty message="No injuries listed for this slate." /> :
        Object.entries(byTeam).sort().map(([team, list]) => (
          <Card key={team} style={{ marginTop: 10, padding: '8px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 6 }}>
              <Logo abbr={team} size={22} /><span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{team}</span>
            </div>
            {list.map((i, k) => (
              <div key={k} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 0', borderTop: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em', color: '#0b0b0b', minWidth: 62, textAlign: 'center',
                  background: STATUS_COLOR[i.status] || T.muted, borderRadius: 4, padding: '2px 4px' }}>{i.status.toUpperCase()}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{i.name}</div>
                  {i.detail && <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.4 }}>{i.detail}</div>}
                </div>
              </div>
            ))}
          </Card>
        ))}
    </div>
  )
}

// ── Record ────────────────────────────────────────────────────────────────────
const LABEL = { points: 'Points', rebounds: 'Rebounds', assists: 'Assists', threes: 'Threes', pra: 'PRA' }
// Public record = Built to hit (the reads the model is calibrated on). The priced board vs
// book lines stays a collapsed research section until it proves an edge; news edges and
// snipes show their own small records, labeled experimental.
const MS_LABEL = { points: 'Points', rebounds: 'Rebounds', assists: 'Assists', threes: 'Threes', pra: 'PRA' }
function RecordView({ season, rec }) {
  const b = rec?.built_to_hit || {}
  const ex = rec?.experimental || {}
  const nw = ex.news || {}, sn = ex.snipes || {}
  const hr = v => v == null ? T.faint : v >= 0.8 ? T.green : v >= 0.7 ? T.amber : T.red
  const stat = (v, label, color) => (
    <div><div style={{ fontFamily: T.mono, fontSize: 22, fontWeight: 900, color: color || T.text }}>{v}</div>
      <div style={{ fontSize: 9.5, color: T.muted, letterSpacing: '.08em' }}>{label}</div></div>
  )
  const line = (label, n, rate, k) => (
    <div key={k} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderTop: `1px solid ${T.border}` }}>
      <span style={{ flex: 1, fontSize: 13, color: T.text, fontWeight: 600 }}>{label}</span>
      <span style={{ fontFamily: T.mono, fontSize: 12.5, color: T.muted2, width: 70, textAlign: 'right' }}>{n}</span>
      <span style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 800, width: 52, textAlign: 'right', color: hr(rate) }}>{pctInt(rate)}</span>
    </div>
  )
  const units = u => u == null ? '—' : `${u >= 0 ? '+' : ''}${u.toFixed(2)}u`
  const nights = Object.entries(b.by_date || {}).reverse()
  return (
    <div>
      <Card style={{ marginTop: 10, padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1, fontSize: 11, fontWeight: 800, color: T.green, letterSpacing: '.08em' }}>🎯 BUILT TO HIT · {season}-{String(season + 1).slice(2)}</div>
          {b.graded > 0 && <button onClick={() => shareBthRecord(rec, season)} style={{ cursor: 'pointer', fontSize: 12,
            fontWeight: 800, color: T.green, background: 'rgba(0,217,126,0.10)', border: `1px solid ${T.green}`,
            borderRadius: 9, padding: '5px 10px' }}>⤴ Share record</button>}
        </div>
        <div style={{ display: 'flex', gap: 22, marginTop: 10, flexWrap: 'wrap' }}>
          {stat(b.graded ? `${b.hits}-${b.misses}` : '—', 'RECORD')}
          {stat(pctInt(b.hit_rate), 'HIT RATE', hr(b.hit_rate))}
          {stat(pctInt(b.expected), 'MODEL SAID')}
          {rec?.replay_hit_rate != null && stat(pctInt(rec.replay_hit_rate), '2025 REPLAY')}
        </div>
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 10, lineHeight: 1.5 }}>
          Each player's single best "N+" read each night: the highest round number the model has
          the player clearing at 80%+, graded on the box score. "Model said" is the average probability shown, so you can see
          it's honest. The 2025 replay is the same reads re-run on last season, out of sample.</div>
      </Card>

      {Object.keys(b.by_prop || {}).length > 0 && <Card style={{ marginTop: 10, padding: '6px 14px 4px' }}>
        <div style={{ display: 'flex', fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '.08em', padding: '6px 0' }}>
          <span style={{ flex: 1 }}>BY STAT</span><span style={{ width: 70, textAlign: 'right' }}>W-L</span><span style={{ width: 52, textAlign: 'right' }}>HIT</span></div>
        {Object.entries(b.by_prop).map(([k, v]) => line(MS_LABEL[k] || k, v.hits != null ? `${v.hits}-${v.misses}` : v.graded, v.hit_rate, k))}
      </Card>}

      {nights.length > 0 && <Section icon="📅" title="By night" meta={`${nights.length} nights graded`}>
        {nights.map(([d, v]) => line(d, v.hits != null ? `${v.hits}-${v.misses}` : v.graded, v.hit_rate, d))}
      </Section>}

      <Section icon="🧪" title="News edges · experimental"
        meta={nw.graded ? `${nw.wins}-${nw.losses} · ${units(nw.units)}` : 'No graded signals yet'}>
        <div style={{ fontSize: 12, color: T.muted2, lineHeight: 1.5 }}>
          Availability news the line hasn't absorbed yet, priced at the posted odds. Small sample; it isn't part of the headline record.</div>
        {line('Record', nw.graded ? `${nw.wins}-${nw.losses}` : '—', nw.graded ? nw.wins / nw.graded : null, 'nw')}
        <div style={{ display: 'flex', padding: '8px 0', borderTop: `1px solid ${T.border}`, fontSize: 13 }}>
          <span style={{ flex: 1, color: T.text, fontWeight: 600 }}>Units · beat the close</span>
          <span style={{ fontFamily: T.mono, color: (nw.units || 0) >= 0 ? T.green : T.red }}>{units(nw.units)}</span>
          <span style={{ fontFamily: T.mono, color: T.muted2, width: 90, textAlign: 'right' }}>
            {nw.clv_beat != null ? `${pctInt(nw.clv_beat)} of ${nw.clv_n}` : '—'}</span>
        </div>
      </Section>

      <Section icon="🎯" title="Snipes · experimental"
        meta={sn.graded ? `${sn.hits} of ${sn.graded} hit · ${units(sn.units)}` : 'No graded snipes yet'}>
        <div style={{ fontSize: 12, color: T.muted2, lineHeight: 1.5 }}>
          Long shots (+300 and longer) after late news. Most of these lose by design, so judge them on units, not hit rate.
          Model expected {sn.expected_hits ?? '—'} hits.</div>
        {line('Hits', sn.graded ? `${sn.hits}/${sn.graded}` : '—', null, 'sn')}
      </Section>

      <Section icon="📊" title="Model board vs book lines · research"
        meta={rec?.edge_proven ? 'Edge proven' : "Not public: hasn't beaten the closing line yet"}>
        <RecordTab season={season} />
      </Section>
    </div>
  )
}

function RecordTab({ season }) {
  const [card, setCard] = useState(null)
  const [res, setRes] = useState(null)
  const [val, setVal] = useState(null)
  useEffect(() => {
    getNBAScorecard(season).then(setCard)
    getNBAResults(season).then(setRes)
    getNBAValidation().then(setVal)
  }, [season])
  if (!card) return <Spinner />
  const o = card.overall || {}
  const wl = r => `${r.wins || 0}-${r.losses || 0}${r.pushes ? `-${r.pushes}` : ''}`
  const row = (label, r, k) => (
    <div key={k} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderTop: `1px solid ${T.border}` }}>
      <span style={{ flex: 1, fontSize: 13, color: T.text, fontWeight: 600 }}>{label}</span>
      <span style={{ fontFamily: T.mono, fontSize: 13, color: T.muted2, width: 70, textAlign: 'right' }}>{wl(r)}</span>
      <span style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 800, width: 52, textAlign: 'right',
        color: r.win_pct == null ? T.faint : r.win_pct >= 0.55 ? T.green : r.win_pct >= 0.5 ? T.amber : T.red }}>{pctInt(r.win_pct)}</span>
    </div>
  )
  const decided = (o.wins || 0) + (o.losses || 0) + (o.pushes || 0)   // DNPs don't count
  const recent = (res?.plays || []).filter(r => r.result !== 'dnp').slice(-25).reverse()
  const markets = val?.markets || {}
  const P = card.priced || {}
  const po = P.overall || {}
  const bets = (po.wins || 0) + (po.losses || 0)
  const signed = (v, d = 1) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`
  const roiRow = (label, r, k) => (
    <div key={k} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderTop: `1px solid ${T.border}` }}>
      <span style={{ flex: 1, fontSize: 13, color: T.text, fontWeight: 600 }}>{label}</span>
      <span style={{ fontFamily: T.mono, fontSize: 12.5, color: T.muted2, width: 64, textAlign: 'right' }}>{wl(r)}</span>
      <span style={{ fontFamily: T.mono, fontSize: 12.5, width: 58, textAlign: 'right',
        color: r.units == null ? T.faint : r.units >= 0 ? T.green : T.red }}>{signed(r.units, 1)}u</span>
      <span style={{ fontFamily: T.mono, fontSize: 12.5, fontWeight: 800, width: 58, textAlign: 'right',
        color: r.roi == null ? T.faint : r.roi >= 0 ? T.green : T.red }}>{r.roi == null ? '—' : `${signed(r.roi * 100)}%`}</span>
    </div>
  )
  const stat = (v, label, color) => (
    <div><div style={{ fontFamily: T.mono, fontSize: 22, fontWeight: 900, color: color || T.text }}>{v}</div>
      <div style={{ fontSize: 9.5, color: T.muted, letterSpacing: '.08em' }}>{label}</div></div>
  )
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '.08em', marginTop: 12 }}>
        PRICED PLAYS · GRADED AT THE POSTED PRICE</div>
      <Card style={{ marginTop: 6, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
        {stat(wl(po), 'RECORD')}
        {stat(po.roi == null ? '—' : `${signed(po.roi * 100)}%`, 'ROI (1U FLAT)', po.roi == null ? T.faint : po.roi >= 0 ? T.green : T.red)}
        {stat(po.clv_beat == null ? '—' : pctInt(po.clv_beat), 'BEAT CLOSE', po.clv_beat == null ? T.faint : po.clv_beat >= 0.5 ? T.green : T.amber)}
      </Card>
      <div style={{ fontSize: 11.5, color: T.muted, marginTop: 8, lineHeight: 1.45 }}>
        Only plays with a real book line, graded at the price the board showed — wins pay the actual
        odds, losses cost 1 unit. <b style={{ color: T.muted2 }}>Beat close</b> = how often the market moved
        toward our side between the first price and tip (closing line value) — the earliest honest
        sign of a real edge{po.clv_n ? ` (${po.clv_n} plays, avg ${signed(po.clv_avg, 2)}pp)` : ''}.
      </div>
      {!bets && <div style={{ fontSize: 12, color: T.muted, marginTop: 10, lineHeight: 1.45 }}>
        No priced plays graded yet for {season}.</div>}
      {bets > 0 && <>
        <SectionLabel>By rating</SectionLabel>
        <Card style={{ padding: '0 12px' }}>
          {P.recommended?.graded > 0 && roiRow('Strong or better', P.recommended, 'reco')}
          {P.value?.graded > 0 && roiRow('VALUE flagged (2pp+)', P.value, 'value')}
          {Object.entries(P.by_tier || {}).map(([k, r]) => roiRow(k === '?' ? 'Unrated' : (TIER_LABEL[k] || k), r, k))}
        </Card>
        <SectionLabel>By market</SectionLabel>
        <Card style={{ padding: '0 12px' }}>{Object.entries(P.by_prop || {}).map(([k, r]) => roiRow(LABEL[k] || k, r, k))}</Card>
        <SectionLabel>By side</SectionLabel>
        <Card style={{ padding: '0 12px' }}>{Object.entries(P.by_side || {}).map(([k, r]) => roiRow(k === 'over' ? 'Overs' : 'Unders', r, k))}</Card>
      </>}
      <SectionLabel>All projections</SectionLabel>
      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 6, lineHeight: 1.45 }}>
        Every projection, including ones with no book line (read at the model's own line, ~50/50 by
        design). Shown for completeness — the priced record above is the one to judge.</div>
      <Card style={{ padding: '0 12px' }}>
        {row('All projections', o, 'all')}
        {Object.entries(card.by_prop || {}).map(([k, r]) => row(`· ${LABEL[k] || k}`, r, 'a' + k))}
      </Card>
      {recent.length > 0 && <>
        <SectionLabel>Latest graded</SectionLabel>
        <Card style={{ padding: '0 12px' }}>
          {recent.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <span style={{ fontSize: 10, fontWeight: 900, width: 18, color: r.result === 'win' ? T.green : r.result === 'loss' ? T.red : T.muted }}>
                {r.result === 'win' ? 'W' : r.result === 'loss' ? 'L' : r.result === 'push' ? 'P' : '—'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text }}>{r.name}</div>
                <div style={{ fontSize: 10.5, color: T.muted }}>{r.date.slice(5)} · {r.predicted === 'over' ? 'O' : 'U'} {r.line} {SHORT[r.prop] || r.prop} · actual {r.actual}</div>
              </div>
              <span style={{ fontFamily: T.mono, fontSize: 12, color: T.muted2 }}>{pctInt(r.prob)}</span>
            </div>
          ))}
        </Card>
      </>}
      {Object.keys(markets).length > 0 && <>
        <SectionLabel>Backtest validation</SectionLabel>
        <Card style={{ padding: '0 12px' }}>
          {Object.entries(markets).map(([k, mk]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderTop: `1px solid ${T.border}` }}>
              <span style={{ flex: 1, fontSize: 13, color: T.text, fontWeight: 600 }}>{LABEL[k] || k}</span>
              <span style={{ fontFamily: T.mono, fontSize: 12, color: T.muted2, marginRight: 10 }}>{mk.hit_rate != null ? `${mk.hit_rate}%` : '—'}</span>
              <span style={{ fontSize: 10, fontWeight: 800, color: mk.verdict === 'PASS' ? G2 : mk.verdict === 'REVIEW' ? T.amber : T.faint }}>{mk.verdict}</span>
            </div>
          ))}
        </Card>
      </>}
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <a href="/nba/proof" style={{ fontSize: 12, fontWeight: 700, color: G2, textDecoration: 'none' }}>Shareable track record →</a>
      </div>
    </div>
  )
}

// ── Coming soon (before any projections exist) ────────────────────────────────
const PREVIEW_ROWS = [
  { name: 'Example Guard', meta: 'G · TmA vs TmB · Points o27.5', proj: 29.1, prob: 0.61, conf: 74, edge: null },
  { name: 'Example Big', meta: 'C · TmC vs TmD · Rebounds o11.5', proj: 12.4, prob: 0.58, conf: 70, edge: 3.2 },
  { name: 'Example Playmaker', meta: 'G · TmE vs TmF · Assists o8.5', proj: 9.1, prob: 0.56, conf: 68, edge: null },
  { name: 'Example Star', meta: 'F · TmI vs TmJ · Pts+Reb+Ast o44.5', proj: 46.3, prob: 0.60, conf: 72, edge: 2.4 },
]
function ComingSoon({ status }) {
  const steps = [
    ['Projection models', 'Points, rebounds, assists, threes & PRA — minutes-driven, per player, per night.', true],
    ['Injury intel', 'OUT players removed, their minutes redistributed; Questionable flagged.', true],
    ['Odds + edges', 'Book lines vs the model — the same fair-price edge as the other boards.', true],
    ['Data feed', 'balldontlie connected — the board fills on the next run.', !!status?.bdl_key_set],
  ]
  return (
    <div style={{ padding: '24px 2px 20px' }}>
      <div style={{ fontSize: 38 }}>🏀</div>
      <div style={{ fontSize: 25, fontWeight: 900, color: T.text, lineHeight: 1.15 }}>The NBA board is<br />warming up</div>
      <div style={{ fontSize: 13.5, color: T.muted, marginTop: 10, lineHeight: 1.5 }}>
        Minutes-driven props built the way they actually move: minutes, pace, rest, matchup and who's out.
      </div>
      <div style={{ marginTop: 18 }}>
        {steps.map(([title, sub, done], i) => (
          <div key={i} style={{ display: 'flex', gap: 12, padding: '11px 2px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
            <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', border: `1.5px solid ${done ? T.green : T.border2}`,
              color: done ? '#04140D' : T.faint, background: done ? T.green : 'transparent', fontSize: 12, fontWeight: 900,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{done ? '✓' : i + 1}</span>
            <div><div style={{ fontSize: 14, fontWeight: 700, color: done ? T.text : T.muted }}>{title}</div>
              <div style={{ fontSize: 12, color: T.faint, marginTop: 1, lineHeight: 1.4 }}>{sub}</div></div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '22px 0 8px' }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: T.text }}>What the board looks like</span>
        <span style={{ fontSize: 9, fontWeight: 800, color: '#1a0d04', background: ORANGE, borderRadius: 4, padding: '2px 6px' }}>EXAMPLE</span>
      </div>
      <Card style={{ padding: '2px 12px' }}>
        {PREVIEW_ROWS.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 2px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text }}>{p.name}</div>
              <div style={{ fontSize: 10.5, color: T.muted }}>{p.meta} · proj {p.proj}</div>
            </div>
            <span style={{ fontFamily: T.mono, fontSize: 12, fontWeight: 800, color: p.edge == null ? T.faint : G2 }}>{p.edge == null ? '—' : `+${p.edge}pp`}</span>
            <span style={{ fontFamily: T.mono, fontSize: 15, fontWeight: 800, color: T.green, minWidth: 40, textAlign: 'right' }}>{pctInt(p.prob)}</span>
          </div>
        ))}
      </Card>
      <div style={{ fontSize: 11, color: T.faint, marginTop: 8 }}>Placeholder names — not live picks.</div>
    </div>
  )
}

// Optional ?season=YYYY override — point the app at a specific season from the URL.
function seasonOverride() {
  try {
    const s = new URLSearchParams(window.location.search).get('season')
    return s && /^\d{4}$/.test(s) ? parseInt(s, 10) : null
  } catch (_) { return null }
}

export default function NBAApp() {
  const forced = seasonOverride()
  const [season, setSeason] = useState(forced || 2026)
  const [status, setStatus] = useState(null)
  const [today, setToday] = useState('')
  const [date, setDate] = useState('')
  const [view, setView] = useState('home')
  const [slate, setSlate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(null)
  const [brk, setBrk] = useState(null)
  const [gameSel, setGameSel] = useState(null)
  const [bthInit, setBthInit] = useState({ prop: '', flt: '' })
  const [help, setHelp] = useState(false)
  const [research, setResearch] = useState({ research_mode: true })
  const [recordStatus, setRecordStatus] = useState({ public: false, nights_graded: 0, nights_needed: 7 })
  const [slip, setSlip] = useState([])
  const [slipOpen, setSlipOpen] = useState(false)
  const legKey = l => `${l.bdl_id}|${l.prop}`
  const inSlip = p => !!p && slip.some(l => legKey(l) === legKey(p))
  const toggleSlip = p => setSlip(inSlip(p) ? slip.filter(l => legKey(l) !== legKey(p)) : [...slip, p])

  useEffect(() => {
    getNBAMeta(forced || undefined).then(mt => {
      if (!forced && mt?.season) setSeason(mt.season)
      setResearch(mt?.research || { research_mode: true })
      setRecordStatus(mt?.record || { public: false, nights_graded: 0, nights_needed: 7 })
      const t = mt?.today || new Date().toISOString().slice(0, 10)
      setToday(t)
      setDate(mt?.latest_slate || t)
    })
  }, [])
  useEffect(() => { getNBAStatus(season).then(setStatus) }, [season])
  useEffect(() => {
    if (!date) return
    let live = true
    setLoading(true)
    getNBASlate(season, date, 4).then(d => { if (live) { setSlate(d); setLoading(false) } })
    return () => { live = false }
  }, [season, date])

  const go = v => { setView(v); try { window.scrollTo(0, 0) } catch (_) {} }
  const live = status?.phase === 'live'
  const showDate = view !== 'record'
  const body = useMemo(() => {
    if (view === 'top') return <TopPlaysTab season={season} date={date} onOpen={setOpen} onBreak={setBrk} />
    if (view === 'lab') return <PlayLabTab season={season} date={date} onBreak={setBrk} inSlip={inSlip} onToggle={toggleSlip} />
    if (view === 'bth') return <BuiltToHitTab key={`${bthInit.prop}|${bthInit.flt}`} season={season} date={date} onBreak={setBrk}
      inSlip={inSlip} onToggle={toggleSlip} initProp={bthInit.prop} initFlt={bthInit.flt} />
    if (view === 'matchups') return <MatchupsTab season={season} date={date} onBreak={setBrk} />
    if (view === 'more') return <MoreTab go={go} onHelp={() => setHelp(true)} season={season} date={date} onOpen={setOpen} />
    if (view === 'board' || view === 'players') return <BoardTab season={season} date={date} onOpen={setOpen} />
    if (view === 'games') return <GamesTab data={slate} loading={loading} onOpen={setOpen} />
    if (view === 'injuries') return <InjuriesTab data={slate} loading={loading} />
    if (view === 'record') return (recordStatus?.public || showRecordOverride())
      ? <RecordView season={season} rec={recordStatus} /> : <RecordTesting rec={recordStatus} />
    return <HomeTab data={slate} loading={loading} season={season} date={date} today={today} onOpen={setOpen} onBreak={setBrk} go={go}
      homeSel={{ v: gameSel, set: setGameSel }} onBuild={(prop, flt = '') => { setBthInit({ prop, flt }); go('bth') }} />
  }, [view, season, date, slate, loading, today, recordStatus, slip, gameSel, bthInit])

  return (
    <ResearchCtx.Provider value={research}>
    <RecordCtx.Provider value={recordStatus}>
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ maxWidth: 460, margin: '0 auto', paddingBottom: 76 }}>
        <Header onHelp={() => setHelp(true)} />
        <div style={{ padding: '0 14px' }}>
          {status == null ? <Spinner />
            : !live ? <ComingSoon status={status} />
            : <>
                {showDate && date && <DateBar date={date} setDate={setDate} today={today} />}
                
                {body}
              </>}
          <div style={{ marginTop: 24, fontSize: 10.5, color: T.faint, textAlign: 'center', lineHeight: 1.5 }}>
            Data: balldontlie.io · The Odds API · Research, not betting advice · 21+ · Gambling problem? 1-800-GAMBLER
          </div>
        </div>
      </div>
      {live && <BottomNav view={view} setView={go} items={NBA_NAV} />}
      {live && slip.length > 0 && !open && !slipOpen && (
        <button onClick={() => setSlipOpen(true)} style={{ position: 'fixed', right: 16, bottom: 78, zIndex: 210, cursor: 'pointer',
          padding: '10px 16px', borderRadius: 24, border: `2px solid ${T.green}`, background: '#0D1117', color: T.green, fontWeight: 900,
          fontSize: 14, boxShadow: '0 6px 18px rgba(0,0,0,.45)' }}>🎟️ Parlay · {slip.length} · {(parlayProb(slip) * 100).toFixed(1)}%</button>)}
      {open && <PlaySheet p={open} onClose={() => setOpen(null)} inSlip={inSlip(open)} onToggleSlip={toggleSlip} />}
      {brk && !open && <BreakdownSheet item={brk} msRecord={slate?.milestones?.record} onClose={() => setBrk(null)}
        onOpenFull={p => { setBrk(null); setOpen(p) }} />}
      {slipOpen && <ParlaySheet legs={slip} setLegs={setSlip} onClose={() => setSlipOpen(false)} />}
      {help && <Glossary onClose={() => setHelp(false)} />}
    </div>
    </RecordCtx.Provider>
    </ResearchCtx.Provider>
  )
}
